import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DBATrackingCell } from "@/components/DBATrackingCell";
import { DbaScanDialog } from "@/components/DbaScanDialog";
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API, getAuthHeader } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Database, Download, Printer, Search, Upload, RefreshCw,
  CalendarRange, ChevronDown, ChevronLeft, ChevronRight,
  X, AlertTriangle, CheckCircle2, History, Loader2, ExternalLink, Trash2, Ban, StickyNote, Package,
  Wand2,
  ScanLine,
} from 'lucide-react';
import CancelNotePopover from './CancelNotePopover';
import { PendingParcelsPanel } from './PendingParcelsPanel';
import { PackingBookImportDialog } from './PackingBookImportDialog';
import { EmiratesTrackingUpdate } from './EmiratesTrackingUpdate';
import { MobileCollapse, useIsMobile } from './MobileKit';
import { DbaMobileCards, DbaRecordSheet } from './DbaMobile';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

// Tracking-number searches ignore the date range so a parcel never hides behind it.
const isTrackingLike = (q) => { const t = (q || '').trim(); return /^[A-Za-z0-9-]{8,}$/.test(t) && (t.match(/\d/g) || []).length >= 5; };

const PERIOD_PRESETS = [
  { id: 'today', label: 'Today', fn: () => { const t = todayStr(); return [t, t]; } },
  { id: '7d', label: 'Last 7 Days', fn: () => [daysAgo(7), todayStr()] },
  { id: '14d', label: 'Bi-Monthly', fn: () => [daysAgo(14), todayStr()] },
  { id: '30d', label: 'Monthly', fn: () => [daysAgo(30), todayStr()] },
  { id: '90d', label: 'Quarterly', fn: () => [daysAgo(90), todayStr()] },
  { id: 'all', label: 'All time', fn: () => ['', ''] },
];

const ISSUE_FILTERS = [
  { id: '', label: 'All Records', color: '' },
  { id: 'parcels', label: 'All Parcels', color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  { id: 'all_errors', label: 'All Errors', color: 'bg-red-100 text-red-700 border-red-300' },
  { id: 'zero_qty', label: 'Zero Qty', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { id: 'zero_weight', label: 'Zero Weight', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  { id: 'blank_service', label: 'Blank Service', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  { id: 'blank_date', label: 'Blank Date', color: 'bg-pink-100 text-pink-700 border-pink-300' },
  { id: 'blank_invoice', label: 'Bad Invoice', color: 'bg-rose-100 text-rose-700 border-rose-300' },
  { id: 'blank_client_code', label: 'Blank Client Code', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { id: 'cancelled', label: 'Cancelled Parcels', color: 'bg-pink-200 text-pink-900 border-pink-400' },
];

const todayStr = () => new Date().toISOString().split('T')[0];
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; };
const fmtDate = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

const DatabaseAPanel = ({ user }) => {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [clientCodes, setClientCodes] = useState([]);
  const [issueCounts, setIssueCounts] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(todayStr());
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedServices, setSelectedServices] = useState([]);
  // "Pending EMI" chip filter — quickly show only auto-generated temp
  // trackings that still need real courier trackings applied.
  const [trackingStatus, setTrackingStatus] = useState(null); // null | 'temp_emi' | 'real'
  // Where-did-this-parcel-go hint — populated when a search yields
  // zero results but matches rows archived by a prior invoice
  // replacement (iter-106). Purely informational.
  const [searchHistoryHint, setSearchHistoryHint] = useState(null);
  const [servicesList, setServicesList] = useState([]);
  const [showServicePicker, setShowServicePicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [issueFilter, setIssueFilter] = useState('');
  // Blank-client-code fix dialog
  const [fixCcOpen, setFixCcOpen] = useState(false);
  const [fixCcLoading, setFixCcLoading] = useState(false);
  const [fixCcData, setFixCcData] = useState({ invoices: [], total_blank: 0, invoices_with_suggestion: 0 });
  const [fixCcOverrides, setFixCcOverrides] = useState({});   // { [invoice_no]: user-typed override }
  const [fixCcApplying, setFixCcApplying] = useState({});     // { [invoice_no]: bool }
  // Emirates one-click sync (replaces per-invoice Apply buttons)
  const emiratesFileRef = useRef(null);
  const perInvoiceFileRef = useRef(null);
  const [perInvoiceTarget, setPerInvoiceTarget] = useState('');
  const [emiratesUploading, setEmiratesUploading] = useState(false);
  const [emiratesResult, setEmiratesResult] = useState(null);
  // Correct wrong / blank client codes from an Order Sheet (per-customer)
  const orderSheetFileRef = useRef(null);
  const [orderSheetInvoice, setOrderSheetInvoice] = useState('');
  const [orderSheetUploading, setOrderSheetUploading] = useState(false);
  const [orderSheetResult, setOrderSheetResult] = useState(null);
  // One-click sweep: EMI-prefixed trackings mis-labelled as EMS/blank → EMIRATES
  const [emiFixRunning, setEmiFixRunning] = useState(false);
  const [emiFixResult, setEmiFixResult] = useState(null);
  // Bulk edit history / one-click revert
  const [bulkHistoryOpen, setBulkHistoryOpen] = useState(false);
  const [bulkHistoryLoading, setBulkHistoryLoading] = useState(false);
  const [bulkHistoryEntries, setBulkHistoryEntries] = useState([]);
  const [bulkReverting, setBulkReverting] = useState({});
  const [bulkPreviewEntry, setBulkPreviewEntry] = useState(null);
  // Blank-date fix dialog
  const [fixDateOpen, setFixDateOpen] = useState(false);
  const [fixDateLoading, setFixDateLoading] = useState(false);
  const [fixDateData, setFixDateData] = useState({ invoices: [], total_blank: 0, invoices_with_suggestion: 0 });
  const [fixDateOverrides, setFixDateOverrides] = useState({}); // { [invoice_no]: "17-Jul-26" }
  const [fixDateApplying, setFixDateApplying] = useState({});
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  // Iter 221 — separate dialog for admin-corrected direct-store uploads.
  const [showCorrected, setShowCorrected] = useState(false);
  const [showPackBook, setShowPackBook] = useState(false);
  const isMobile = useIsMobile();
  const [mobileRec, setMobileRec] = useState(null);
  const [packBookFile, setPackBookFile] = useState(null);
  const [ccFile, setCcFile] = useState(null);         // corrected sheet
  const [ccBulk, setCcBulk] = useState(null);         // optional bulk data
  const [ccWeight, setCcWeight] = useState(null);     // optional weight-only sheet
  const [ccClientCode, setCcClientCode] = useState('');
  const [ccUploading, setCcUploading] = useState(false);
  // Iter 222 — upload history for one-click undo.
  const [ccHistory, setCcHistory] = useState([]);
  const [ccHistoryOpen, setCcHistoryOpen] = useState(false);
  const [ccUndoBusy, setCcUndoBusy] = useState("");
  // Iter 252 — multi-tab packing-list → bulk weight apply.
  const [showWeightSheet, setShowWeightSheet] = useState(false);
  const [wsFile, setWsFile] = useState(null);
  const [wsOverwrite, setWsOverwrite] = useState(false);
  const [wsBusy, setWsBusy] = useState(false);
  const [wsResult, setWsResult] = useState(null);
  const [wsDiffTab, setWsDiffTab] = useState(null); // null | 'diffs' | 'unmatched'
  const applyWeightSheet = useCallback(async () => {
    if (!wsFile) { toast.error("Pick a packing-list workbook first"); return; }
    setWsBusy(true); setWsResult(null);
    try {
      const fd = new FormData();
      fd.append('weight_sheet', wsFile);
      fd.append('overwrite', wsOverwrite ? 'true' : 'false');
      const r = await axios.post(`${API}/database-a/apply-weight-sheet`,
        fd, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      setWsResult(r.data);
      toast.success(`Updated ${r.data.updated_rows} rows across ${r.data.parcels} parcels`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally { setWsBusy(false); }
  }, [wsFile, wsOverwrite]);
  const fetchCcHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/database-a/corrected-uploads`,
        { headers: getAuthHeader() });
      setCcHistory(r.data.uploads || []);
    } catch { setCcHistory([]); }
  }, []);

  const [plFile, setPlFile] = useState(null);
  const [bdFile, setBdFile] = useState(null);
  const [osFile, setOsFile] = useState(null);
  const [uploadClientCode, setUploadClientCode] = useState('');
  const [uploading, setUploading] = useState(false);
  // Iter 225 — live upload progress state (upload %, processing timer).
  // Phase toggles between 'upload' (bytes to server) and 'processing'
  // (server crunching rows), each with an elapsed clock.
  const [ulPhase, setUlPhase] = useState('');       // '', 'upload', 'processing'
  const [ulPct, setUlPct] = useState(0);
  const [ulStarted, setUlStarted] = useState(0);
  const [ulElapsed, setUlElapsed] = useState(0);
  useEffect(() => {
    if (!ulPhase || !ulStarted) return;
    const t = setInterval(() => setUlElapsed(Math.round((Date.now() - ulStarted) / 100) / 10), 200);
    return () => clearInterval(t);
  }, [ulPhase, ulStarted]);
  const beginProgress = () => {
    setUlPhase('upload'); setUlPct(0); setUlStarted(Date.now()); setUlElapsed(0);
  };
  const endProgress = () => {
    setUlPhase(''); setUlPct(0); setUlStarted(0); setUlElapsed(0);
    setUlJob(null);
  };
  const onUploadProgress = (e) => {
    if (!e.total) return;
    const p = Math.min(100, Math.round((e.loaded / e.total) * 100));
    setUlPct(p);
    if (p >= 100) { setUlPhase('processing'); setUlStarted(Date.now()); }
  };

  // Iter 226 — run an upload as a background job and poll for N-of-M
  // row progress. Falls back to the plain response when the server
  // processed synchronously.
  const [ulJob, setUlJob] = useState(null);  // {phase, processed, total}
  const runUploadJob = async (url, form) => {
    form.append('background', 'true');
    const r = await axios.post(url, form, {
      headers: getAuthHeader(),
      onUploadProgress,
    });
    const jobId = r.data?.job_id;
    if (!jobId) return r.data || {};
    setUlPhase('processing');
    for (;;) {
      await new Promise(res => setTimeout(res, 600));
      const jr = await axios.get(`${API}/database-a/upload-job/${jobId}`,
        { headers: getAuthHeader() });
      const j = jr.data || {};
      setUlJob(j);
      if (j.phase === 'done') return j.result || {};
      if (j.phase === 'error') throw new Error(j.error || 'Upload failed');
    }
  };

  // Missing Weight Sources card — hidden by default per user request; the
  // dedicated toolbar button (next to History) opens it when needed.
  const [missingWeights, setMissingWeights] = useState({ missing_invoices: [], total_missing_parcels: 0, total_missing_invoices: 0 });
  const [showMissingCard, setShowMissingCard] = useState(false);
  const [perInvUploading, setPerInvUploading] = useState('');

  // Edit Audit History
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTrackFilter, setHistoryTrackFilter] = useState('');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');

  // Row selection (double-click tracking cell toggles)
  const [selectedRows, setSelectedRows] = useState(new Set()); // keys: "track_no::product"
  const [deleting, setDeleting] = useState(false);

  // Admin Bulk Purge (Iter 137) — filter-based destructive delete gated by
  // supervisor password `Om@88`. Admin only.
  // Iter 178 — "Delete Entire Day" mode: one date wipes all services/customers.
  const [adminPurgeOpen, setAdminPurgeOpen] = useState(false);
  const [adminPurge, setAdminPurge] = useState({
    date_from: '', date_to: '', customer_name: '', service: '', password: '',
  });
  const [purgeMode, setPurgeMode] = useState('custom'); // 'custom' | 'day' | 'restore'
  const [purgeDay, setPurgeDay] = useState('');
  const [adminPurgePreview, setAdminPurgePreview] = useState(null); // {matched, preview, service_breakdown, filters}
  const [adminPurgeRunning, setAdminPurgeRunning] = useState(false);

  // Iter 179 — 24h restore + password rotation state
  const [purgeBatches, setPurgeBatches] = useState([]);
  const [restorePassword, setRestorePassword] = useState('');
  const [restoringBatch, setRestoringBatch] = useState('');
  const [pwdRotateOpen, setPwdRotateOpen] = useState(false);
  const [pwdRotate, setPwdRotate] = useState({ current: '', next: '', confirm: '' });
  const [pwdRotateBusy, setPwdRotateBusy] = useState(false);
  const [pwdRotateMeta, setPwdRotateMeta] = useState(null);

  const fetchPurgeBatches = async () => {
    try {
      const r = await axios.get(`${API}/database-a/admin-purge/batches`, { headers: getAuthHeader() });
      setPurgeBatches(r.data.batches || []);
    } catch (e) {
      // silent — surface via inline UI hint instead of a toast every mount
    }
  };

  const runRestoreBatch = async (batchId) => {
    if (!restorePassword) { toast.error('Enter the supervisor password to restore'); return; }
    setRestoringBatch(batchId);
    try {
      const res = await axios.post(
        `${API}/database-a/admin-purge/restore`,
        { purge_batch_id: batchId, password: restorePassword.trim() },
        { headers: getAuthHeader() },
      );
      toast.success(`Restored ${res.data.restored} row${res.data.restored === 1 ? '' : 's'}${res.data.skipped ? ` (${res.data.skipped} skipped — already present)` : ''}`);
      setPurgeBatches(prev => prev.filter(b => b.purge_batch_id !== batchId));
      fetchData(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoringBatch('');
    }
  };

  const fetchPurgePwdMeta = async () => {
    try {
      const r = await axios.get(`${API}/admin/purge-password`, { headers: getAuthHeader() });
      setPwdRotateMeta(r.data);
    } catch { /* non-admin — silent */ }
  };

  const runPwdRotate = async () => {
    if (!pwdRotate.current || !pwdRotate.next) { toast.error('Both fields required'); return; }
    if (pwdRotate.next !== pwdRotate.confirm) { toast.error('New password and confirmation do not match'); return; }
    if (pwdRotate.next.length < 4) { toast.error('New password must be at least 4 characters'); return; }
    setPwdRotateBusy(true);
    try {
      await axios.put(
        `${API}/admin/purge-password`,
        { current_password: pwdRotate.current.trim(), new_password: pwdRotate.next.trim() },
        { headers: getAuthHeader() },
      );
      toast.success('Supervisor purge password rotated');
      setPwdRotate({ current: '', next: '', confirm: '' });
      setPwdRotateOpen(false);
      fetchPurgePwdMeta();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rotate failed');
    } finally {
      setPwdRotateBusy(false);
    }
  };

  const runAdminPurge = async (dryRun) => {
    const { date_from, date_to, customer_name, service } = adminPurge;
    const password = (adminPurge.password || '').trim();
    if (!password) { toast.error('Enter the supervisor password to proceed'); return; }
    let payload;
    if (purgeMode === 'day') {
      if (!purgeDay) { toast.error('Pick the date to wipe'); return; }
      payload = { date_from: purgeDay, date_to: purgeDay, customer_name: '', service: '', password, dry_run: !!dryRun };
    } else {
      if (!date_from && !date_to && !customer_name.trim() && !service.trim()) {
        toast.error('Pick at least one filter (date, client, or service)'); return;
      }
      payload = { date_from, date_to, customer_name, service, password, dry_run: !!dryRun };
    }
    setAdminPurgeRunning(true);
    try {
      const res = await axios.post(
        `${API}/database-a/admin-bulk-delete`,
        payload,
        { headers: getAuthHeader() },
      );
      if (dryRun) {
        setAdminPurgePreview(res.data);
        toast.success(`Preview: ${res.data.matched} row${res.data.matched === 1 ? '' : 's'} match`);
      } else {
        toast.success(`Purged ${res.data.deleted} row${res.data.deleted === 1 ? '' : 's'} from Database A`);
        setAdminPurgeOpen(false);
        setAdminPurge({ date_from: '', date_to: '', customer_name: '', service: '', password: '' });
        setPurgeDay('');
        setAdminPurgePreview(null);
        fetchData(1);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setAdminPurgeRunning(false);
    }
  };

  // Cancel-reason popover (only for cancelled rows)
  const [cancelPopover, setCancelPopover] = useState(null); // { rec, anchorEl }
  const hoverTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openCancelPopover = useCallback((rec, anchorEl) => {
    if (!rec?.cancelled) return;
    cancelClose();
    setCancelPopover({ rec, anchorEl });
  }, [cancelClose]);

  const closeCancelPopover = useCallback(() => {
    cancelClose();
    setCancelPopover(null);
  }, [cancelClose]);

  // Schedule a close — used when cursor leaves the row OR the popover. Skipped
  // if the textarea inside the popover currently has focus (user is editing).
  const scheduleClose = useCallback((delay = 250) => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      const active = document.activeElement;
      const inPopover = active && active.closest && active.closest('[data-testid="cancel-note-popover"]');
      if (inPopover) return; // user is editing — don't yank it away
      setCancelPopover(null);
    }, delay);
  }, [cancelClose]);

  const handleRowMouseEnter = useCallback((rec, anchorEl) => {
    if (!rec?.cancelled) return;
    cancelClose();
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => openCancelPopover(rec, anchorEl), 450);
  }, [openCancelPopover, cancelClose]);

  const handleRowMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    // Only auto-close if a popover is actually showing
    if (cancelPopover) scheduleClose(250);
  }, [cancelPopover, scheduleClose]);

  const rowKey = useCallback((r) => `${r.track_no || ''}::${r.product || ''}`, []);
  const isSelected = useCallback((r) => selectedRows.has(rowKey(r)), [selectedRows, rowKey]);
  const toggleSelect = useCallback((r) => {
    if (!r.track_no) return;
    const key = rowKey(r);
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, [rowKey]);
  const clearSelection = useCallback(() => setSelectedRows(new Set()), []);

  const buildHistoryParams = useCallback((extra = {}) => {
    const params = new URLSearchParams();
    if (historyTrackFilter) params.set('track_no', historyTrackFilter);
    if (historyDateFrom) params.set('date_from', historyDateFrom);
    if (historyDateTo) params.set('date_to', historyDateTo);
    Object.entries(extra).forEach(([k, v]) => params.set(k, v));
    return params;
  }, [historyTrackFilter, historyDateFrom, historyDateTo]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = buildHistoryParams({ limit: 200 });
      const res = await axios.get(`${API}/database-a/history?${params}`, { headers: getAuthHeader() });
      setHistoryItems(res.data.items || []);
    } catch {
      toast.error('Failed to load edit history');
    } finally {
      setHistoryLoading(false);
    }
  }, [buildHistoryParams]);

  // Revert is privileged — gated by a shared team password. Stored as plaintext
  // here because this is a low-risk internal tool with no external exposure,
  // and the audit log records who issued the revert. Per user request: Esr@88.
  const REVERT_PASSWORD = 'Esr@88';

  const revertHistoryEntry = useCallback(async (audit) => {
    const pw = window.prompt(
      `Revert ${audit.track_no} • ${audit.field} back to "${audit.old_value ?? ''}"?\n\nEnter team password to confirm:`
    );
    if (pw === null) return;
    if (pw !== REVERT_PASSWORD) {
      toast.error('Wrong password — revert cancelled');
      return;
    }
    try {
      await axios.post(`${API}/database-a/history/${audit.id}/revert`, {}, { headers: getAuthHeader() });
      toast.success('Reverted — change logged');
      await fetchHistory();
      // Signal DatabaseA to refresh its main table without us capturing fetchData here
      // (avoids a Temporal-Dead-Zone error since fetchData is defined later in this component).
      window.dispatchEvent(new CustomEvent('database-a-refresh'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revert failed');
    }
  }, [fetchHistory]);

  const downloadHistoryExcel = useCallback(async () => {
    try {
      const params = buildHistoryParams();
      const res = await axios.get(
        `${API}/database-a/history/excel?${params}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = (historyDateFrom || historyDateTo)
        ? `_${historyDateFrom || 'start'}_to_${historyDateTo || 'today'}` : '';
      a.download = `database_a_history${suffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 800);
      toast.success('History Excel downloaded');
    } catch {
      toast.error('Excel download failed');
    }
  }, [buildHistoryParams, historyDateFrom, historyDateTo]);

  // Save a single field edit + refresh the visible table
  const saveCellEdit = useCallback(async (rec, field, value) => {
    try {
      const res = await axios.patch(
        `${API}/database-a/record`,
        { track_no: rec.track_no, product: rec.product || '', field, value },
        { headers: getAuthHeader() }
      );
      // Backend tells us if the cancellation flag got toggled (weight field only).
      const cancelledToggle = res.data?.cancelled;
      // Optimistic local update so the user sees the change instantly. Every
      // sibling row of the same parcel needs the cancelled flag mirrored too.
      setRecords(prev => prev.map(r => {
        const samePair = r.track_no === rec.track_no && r.product === rec.product;
        const sameParcel = r.track_no === rec.track_no;
        if (!samePair && !sameParcel) return r;
        const patch = {};
        if (samePair) {
          patch[field] = (field === 'qty' || field === 'weight') ? (parseInt(value, 10) || 0) : value;
        }
        if (cancelledToggle !== null && cancelledToggle !== undefined && sameParcel) {
          patch.cancelled = !!cancelledToggle;
        }
        return { ...r, ...patch };
      }));
      toast.success('Saved — logged to history', { duration: 1600 });
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
      return false;
    }
  }, []);

  const dateLabel = useMemo(() => (isTrackingLike(searchQuery) ? "All dates · tracking search" : !dateFrom && !dateTo ? "All time" : `${dateFrom ? fmtDate(dateFrom) : "Start"} — ${dateTo ? fmtDate(dateTo) : "Today"}`), [dateFrom, dateTo, searchQuery]);
  const LIMIT = 50;

  // Load the blank-client-code invoices for the fix dialog.
  const openFixCcDialog = useCallback(async () => {
    setFixCcOpen(true);
    setFixCcLoading(true);
    setFixCcOverrides({});
    try {
      const r = await axios.get(`${API}/database-a/blank-client-codes`, { headers: getAuthHeader() });
      setFixCcData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load blank client codes");
      setFixCcData({ invoices: [], total_blank: 0, invoices_with_suggestion: 0 });
    } finally {
      setFixCcLoading(false);
    }
  }, []);

  // Auto-open the Fix Blank Client Codes dialog when landed with ?fixBlank=1
  // (used by the header's "open in new tab" arrow so users can work in a
  // full-page context).
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('fixBlank') === '1') {
        openFixCcDialog();
        // Clean up so a refresh doesn't re-open it
        params.delete('fixBlank');
        const clean = window.location.pathname + (params.toString() ? `?${params}` : '');
        window.history.replaceState({}, '', clean);
      }
    } catch { /* silent */ }
  }, [openFixCcDialog]);

  const openBulkHistoryDialog = async () => {
    setBulkHistoryOpen(true);
    setBulkHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/database-a/edit-history`, { headers: getAuthHeader() });
      setBulkHistoryEntries(r.data.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
      setBulkHistoryEntries([]);
    } finally { setBulkHistoryLoading(false); }
  };

  const revertBulkEntry = async (entryId) => {
    setBulkReverting(s => ({ ...s, [entryId]: true }));
    try {
      const r = await axios.post(
        `${API}/database-a/edit-history/${entryId}/revert`, {},
        { headers: getAuthHeader() },
      );
      toast.success(r.data.reverted_before
        ? "Already reverted"
        : `Reverted ${r.data.restored} row${r.data.restored === 1 ? '' : 's'} (${r.data.action})`);
      const refresh = await axios.get(`${API}/database-a/edit-history`, { headers: getAuthHeader() });
      setBulkHistoryEntries(refresh.data.entries || []);
      fetchData(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revert failed");
    } finally {
      setBulkReverting(s => { const c = { ...s }; delete c[entryId]; return c; });
    }
  };

  // Emirates one-click sync: user drops an Emirates courier sheet
  // (XLSX per-invoice tabs OR CSV with invoice column) → backend replaces
  // tracking, weight, and auto-fills client_code for the matching rows.
  const openEmiratesFilePicker = () => {
    if (emiratesFileRef.current) {
      emiratesFileRef.current.value = "";
      emiratesFileRef.current.click();
    }
  };

  const openPerInvoicePicker = (invoiceNo) => {
    setPerInvoiceTarget(invoiceNo || '');
    setTimeout(() => {
      if (perInvoiceFileRef.current) {
        perInvoiceFileRef.current.value = "";
        perInvoiceFileRef.current.click();
      }
    }, 0);
  };

  const handlePerInvoiceFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !perInvoiceTarget) { setPerInvoiceTarget(''); return; }
    setEmiratesUploading(true);
    setEmiratesResult(null);
    try {
      const fd = new FormData();
      fd.append("emirates_sheet", file);
      fd.append("invoice_no_override", perInvoiceTarget);
      const r = await axios.post(
        `${API}/database-a/emirates-sync`, fd,
        { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }, timeout: 120000 },
      );
      setEmiratesResult(r.data);
      toast.success(`${perInvoiceTarget}: ${r.data.updated} updated · ${r.data.unmatched_count} unmatched`);
      fetchData(1);
      const refresh = await axios.get(`${API}/database-a/blank-client-codes`, { headers: getAuthHeader() });
      setFixCcData(refresh.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Emirates sync failed");
    } finally {
      setEmiratesUploading(false);
      setPerInvoiceTarget('');
    }
  };

  const handleEmiratesFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEmiratesUploading(true);
    setEmiratesResult(null);
    try {
      const fd = new FormData();
      fd.append("emirates_sheet", file);
      // If a CSV lacks an Invoice column, allow the filename stem as a hint.
      // (Backend re-uses the filename stem itself when no override is sent.)
      const r = await axios.post(
        `${API}/database-a/emirates-sync`,
        fd,
        { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }, timeout: 120000 },
      );
      setEmiratesResult(r.data);
      toast.success(`Processed ${r.data.processed} · Updated ${r.data.updated} · Unmatched ${r.data.unmatched_count} · Duplicates ${r.data.duplicates_count}`);
      // Refresh main table + blank-client-code list (some rows may now be fixed)
      fetchData(1);
      if (fixCcOpen) {
        try {
          const refresh = await axios.get(`${API}/database-a/blank-client-codes`, { headers: getAuthHeader() });
          setFixCcData(refresh.data);
        } catch { /* silent */ }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Emirates sync failed");
    } finally {
      setEmiratesUploading(false);
    }
  };

  const openOrderSheetFilePicker = (invoiceNo = '') => {
    setOrderSheetInvoice(invoiceNo || '');
    if (orderSheetFileRef.current) {
      orderSheetFileRef.current.value = "";
      orderSheetFileRef.current.click();
    }
  };

  const handleOrderSheetFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOrderSheetUploading(true);
    setOrderSheetResult(null);
    try {
      const fd = new FormData();
      fd.append("order_sheet", file);
      if (orderSheetInvoice) fd.append("invoice_no", orderSheetInvoice);
      const r = await axios.post(
        `${API}/database-a/fix-client-codes-from-order-sheet`,
        fd,
        { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }, timeout: 120000 },
      );
      setOrderSheetResult(r.data);
      toast.success(
        `Invoice ${r.data.invoice_no} · ${r.data.rows_updated} row${r.data.rows_updated === 1 ? '' : 's'} corrected · ${r.data.unmatched_count} unmatched`,
      );
      fetchData(1);
      if (fixCcOpen) {
        try {
          const refresh = await axios.get(`${API}/database-a/blank-client-codes`, { headers: getAuthHeader() });
          setFixCcData(refresh.data);
        } catch { /* silent */ }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Order-sheet correction failed");
    } finally {
      setOrderSheetUploading(false);
      setOrderSheetInvoice('');
    }
  };

  // Export the unmatched rows from the last sync as a CSV so the user can
  // fix names / invoices in Pack Print, then re-run the sync in one loop.
  const downloadUnmatchedCsv = () => {
    const rows = emiratesResult?.unmatched || [];
    if (rows.length === 0) { toast.info("Nothing to export"); return; }
    const cols = ["invoice_no", "customer", "tracking", "weight", "sheet", "reason"];
    const esc = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [cols.join(",")];
    for (const r of rows) lines.push(cols.map(c => esc(r[c])).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = new Date().toISOString().slice(0, 10);
    a.href = url; a.download = `Emirates_Unmatched_${ts}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const applyFixCc = async (invoiceNo, codeToApply) => {
    const code = (codeToApply || '').trim().toUpperCase();
    if (!code) { toast.error("Please pick or type a client code"); return; }
    if (invoiceNo === "(blank invoice)") {
      toast.error("Rows with a blank invoice can't be fixed here — please edit them individually.");
      return;
    }
    setFixCcApplying(s => ({ ...s, [invoiceNo]: true }));
    try {
      const r = await axios.post(
        `${API}/database-a/fix-blank-client-code`,
        { invoice_no: invoiceNo, client_code: code },
        { headers: getAuthHeader() },
      );
      toast.success(`Applied ${code} to ${r.data.modified} row${r.data.modified === 1 ? '' : 's'} on ${invoiceNo}`);
      // Refresh the dialog list + the underlying table counts
      const refresh = await axios.get(`${API}/database-a/blank-client-codes`, { headers: getAuthHeader() });
      setFixCcData(refresh.data);
      fetchData(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply fix");
    } finally {
      setFixCcApplying(s => { const c = { ...s }; delete c[invoiceNo]; return c; });
    }
  };

  // ── Blank Date fix ─────────────────────────────────────────────────────────
  const openFixDateDialog = useCallback(async () => {
    setFixDateOpen(true);
    setFixDateLoading(true);
    setFixDateOverrides({});
    try {
      const r = await axios.get(`${API}/database-a/blank-dates`, { headers: getAuthHeader() });
      setFixDateData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load blank dates");
      setFixDateData({ invoices: [], total_blank: 0, invoices_with_suggestion: 0 });
    } finally {
      setFixDateLoading(false);
    }
  }, []);

  const applyFixDate = async (invoiceNo, sibling) => {
    // sibling = {parcel_date, parcel_date_iso} OR a plain user-typed date string
    let parcel_date = '';
    let parcel_date_iso = '';
    if (typeof sibling === 'object' && sibling !== null) {
      parcel_date = (sibling.parcel_date || '').trim();
      parcel_date_iso = (sibling.parcel_date_iso || '').trim();
    } else {
      parcel_date = (sibling || '').trim();
    }
    if (!parcel_date) { toast.error("Please pick or type a date"); return; }
    if (invoiceNo === "(blank invoice)") {
      toast.error("Rows with a blank invoice can't be bulk-fixed here — please edit them individually.");
      return;
    }
    setFixDateApplying(s => ({ ...s, [invoiceNo]: true }));
    try {
      const r = await axios.post(
        `${API}/database-a/fix-blank-date`,
        { invoice_no: invoiceNo, parcel_date, parcel_date_iso },
        { headers: getAuthHeader() },
      );
      toast.success(`Applied ${parcel_date} to ${r.data.modified} row${r.data.modified === 1 ? '' : 's'} on ${invoiceNo}`);
      const refresh = await axios.get(`${API}/database-a/blank-dates`, { headers: getAuthHeader() });
      setFixDateData(refresh.data);
      fetchData(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply fix");
    } finally {
      setFixDateApplying(s => { const c = { ...s }; delete c[invoiceNo]; return c; });
    }
  };

  const fetchData = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const allDates = isTrackingLike(searchQuery);
      const params = new URLSearchParams({ date_from: allDates ? '' : dateFrom, date_to: allDates ? '' : dateTo, page: p, limit: LIMIT });
      if (selectedClient) params.set('client_code', selectedClient);
      if (selectedServices.length > 0) params.set('service', selectedServices.join(','));
      if (trackingStatus) params.set('tracking_status', trackingStatus);
      if (searchQuery) params.set('search', searchQuery);
      if (issueFilter) params.set('issues', issueFilter);
      const res = await axios.get(`${API}/database-a?${params}`, { headers: getAuthHeader() });
      setRecords(res.data.records);
      setTotal(res.data.total);
      setPages(res.data.pages);
      setPage(p);
      setClientCodes(res.data.client_codes);

      // Empty-search hint — only fire when a search term produced no
      // rows so we don't add extra latency to normal browsing.
      if ((res.data.records || []).length === 0 && searchQuery && searchQuery.trim().length >= 3) {
        try {
          const h = await axios.get(`${API}/database-a/search-history-hint`, {
            params: { q: searchQuery.trim() },
            headers: getAuthHeader(),
          });
          setSearchHistoryHint(h.data);
        } catch { setSearchHistoryHint(null); }
      } else {
        setSearchHistoryHint(null);
      }
      // Merge server-observed services with the canonical list so common
      // couriers still appear even if no data yet.
      const canonical = ['EMIRATES', 'ITPS', 'CMS', 'EMS', 'RMS', 'DHL', 'FedEx'];
      const seen = new Set((res.data.services || []).map(s => (s || '').toUpperCase()));
      const combined = [
        ...canonical.filter(s => seen.has(s.toUpperCase()) || !res.data.services),
        ...(res.data.services || []).filter(s => !canonical.some(c => c.toUpperCase() === (s || '').toUpperCase())),
      ];
      setServicesList(combined);
      setIssueCounts(res.data.issue_counts || {});
      fetchStats();
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, selectedClient, selectedServices, searchQuery, issueFilter, trackingStatus]);

  const handleFixEmiService = useCallback(async () => {
    if (emiFixRunning) return;
    if (!window.confirm(
      'Sweep Database A and re-label every EMI-prefixed tracking currently ' +
      'marked as EMS/blank back to EMIRATES?\n\n' +
      'Only rows where the tracking starts with "EMI" and the service is ' +
      'blank or "EMS" are touched. The whole batch is revertible from ' +
      'Bulk Edits history.'
    )) return;
    setEmiFixRunning(true);
    setEmiFixResult(null);
    try {
      const r = await axios.post(
        `${API}/database-a/fix-emi-service-classification`,
        {},
        { headers: getAuthHeader(), timeout: 60000 },
      );
      setEmiFixResult(r.data);
      const editId = r.data.edit_id;
      toast.success(
        `EMI service fix · ${r.data.fixed} row${r.data.fixed === 1 ? '' : 's'} re-labelled to EMIRATES`,
        editId ? {
          duration: 12000,
          action: {
            label: 'Undo',
            onClick: async () => {
              try {
                const rev = await axios.post(
                  `${API}/database-a/edit-history/${editId}/revert`,
                  {}, { headers: getAuthHeader() },
                );
                toast.success(
                  `EMI fix reverted · ${rev.data?.restored ?? '?'} row${(rev.data?.restored ?? 0) === 1 ? '' : 's'} restored`,
                );
                fetchData(page);
              } catch (revErr) {
                toast.error(revErr?.response?.data?.detail || 'Undo failed');
              }
            },
          },
        } : undefined,
      );
      fetchData(page);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'EMI fix failed');
    } finally {
      setEmiFixRunning(false);
    }
  }, [emiFixRunning, page, fetchData]);


  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/database-a/stats`, { headers: getAuthHeader() });
      setStats(res.data);
    } catch {}
  }, []);

  // Bulk-delete selected rows. Frontend gates behind team password (Esr@88)
  // before hitting the backend — same UX as Revert in audit history.
  const deleteSelected = useCallback(async () => {
    if (selectedRows.size === 0) return;
    const count = selectedRows.size;
    const pwd = window.prompt(
      `Delete ${count} selected row${count > 1 ? 's' : ''} from Database A?\n\nThis cannot be undone. Enter team password to confirm:`
    );
    if (pwd === null) return;
    if (pwd !== 'Esr@88') { toast.error('Wrong password'); return; }

    setDeleting(true);
    try {
      const rows = [];
      selectedRows.forEach((k) => {
        const [track_no, ...rest] = k.split('::');
        rows.push({ track_no, product: rest.join('::') });
      });
      const res = await axios.post(
        `${API}/database-a/bulk-delete`,
        { rows },
        { headers: getAuthHeader() }
      );
      const n = res.data?.deleted || 0;
      toast.success(`${n} row${n === 1 ? '' : 's'} deleted`);
      clearSelection();
      fetchData(page);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  }, [selectedRows, page, clearSelection, fetchData]);

  // Merge Duplicate Cards — manual verification workflow.
  //   1. Click "Merge Duplicates" → scan → open verification modal
  //   2. User reviews groups (Original + Duplicates), ticks which to remove
  //   3. Confirm → /merge-selected → refresh table
  // Composite key: (parcel_date_iso, customer_name, invoice_no, product, qty).
  // Never creates records. Audit-logged for revert.
  const [merging, setMerging] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupLoading, setDupLoading] = useState(false);
  const [dupData, setDupData] = useState({ groups: [], group_count: 0, total_duplicate_records: 0, mode: 'default' });
  const [dupChecked, setDupChecked] = useState({}); // { [record_id]: true }
  const [dupExpanded, setDupExpanded] = useState({}); // { [groupIndex]: true }
  const [dupMode, setDupMode] = useState('default'); // 'default' | 'cross_date'
  // Live search inside the Verify & Merge dialog — filters groups by
  // tracking, product, customer, or invoice. Client-side only so
  // large scans don't need extra API round-trips.
  const [dupSearch, setDupSearch] = useState('');
  // Iter 251 — optional monthly window for the scan ("clear monthly
  // data"). Empty = all time (previous behaviour).
  const [dupFrom, setDupFrom] = useState('');
  const [dupTo, setDupTo] = useState('');

  const mergeDuplicates = useCallback(async (mode = 'default', from = '', to = '') => {
    setDupMode(mode);
    setDupFrom(from);
    setDupTo(to);
    setDupOpen(true);
    setDupLoading(true);
    setDupSearch('');
    setDupData({ groups: [], group_count: 0, total_duplicate_records: 0, mode });
    try {
      const params = { mode };
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const r = await axios.get(`${API}/database-a/duplicate-groups`,
        { params, headers: getAuthHeader() });
      setDupData(r.data);
      // Default: tick every duplicate row (originals are never ticked)
      const preTicked = {};
      const initExpanded = {};
      (r.data.groups || []).forEach((g, gi) => {
        initExpanded[gi] = gi < 5; // auto-expand first 5 groups
        (g.records || []).forEach(rec => {
          if (rec.role === 'duplicate') preTicked[rec.id] = true;
        });
      });
      setDupChecked(preTicked);
      setDupExpanded(initExpanded);
      if (r.data.group_count === 0) {
        toast.success(mode === 'cross_date'
          ? 'No cross-date duplicates found for any invoice'
          : 'No duplicates found — Database A is clean');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to scan duplicates');
    } finally {
      setDupLoading(false);
    }
  }, []);

  const dupToggle = useCallback((rid) => {
    setDupChecked(prev => ({ ...prev, [rid]: !prev[rid] }));
  }, []);

  const dupSelectAll = useCallback(() => {
    setDupChecked(prev => {
      const next = { ...prev };
      (dupData.groups || []).forEach(g =>
        (g.records || []).forEach(rec => {
          if (rec.role === 'duplicate') next[rec.id] = true;
        }));
      return next;
    });
  }, [dupData]);

  const dupDeselectAll = useCallback(() => {
    setDupChecked({});
  }, []);

  const dupSelectedCount = useMemo(() =>
    Object.values(dupChecked).filter(Boolean).length,
  [dupChecked]);

  // Manual "Set as Original" — swap roles inside a group so the ticked
  // record becomes the one kept during merge. Useful in cross-date mode
  // when the auto-picked heuristic isn't the parcel the operator wants
  // to preserve.
  const dupSetOriginal = useCallback((groupIndex, recordId) => {
    setDupData(prev => {
      const groups = [...(prev.groups || [])];
      const g = groups[groupIndex];
      if (!g) return prev;
      const newRecords = g.records.map(r => ({
        ...r,
        role: r.id === recordId ? 'original' : 'duplicate',
      }));
      groups[groupIndex] = { ...g, records: newRecords };
      return { ...prev, groups };
    });
    setDupChecked(prev => {
      const next = { ...prev };
      // Uncheck the newly-elected original, and tick all other duplicates
      const g = (dupData.groups || [])[groupIndex];
      if (g) {
        g.records.forEach(rec => {
          if (rec.id === recordId) next[rec.id] = false;
          else next[rec.id] = true;
        });
      }
      return next;
    });
  }, [dupData]);

  const dupApply = useCallback(async () => {
    if (!dupSelectedCount) {
      toast.error('Select at least one duplicate row');
      return;
    }
    const groups = (dupData.groups || [])
      .map(g => {
        const original = g.records.find(r => r.role === 'original');
        const remove = g.records
          .filter(r => r.role === 'duplicate' && dupChecked[r.id])
          .map(r => r.id);
        return original && remove.length ? { keep_id: original.id, remove_ids: remove } : null;
      })
      .filter(Boolean);

    if (!groups.length) {
      toast.error('No records ticked for merge');
      return;
    }

    const affected = groups.reduce((n, g) => n + g.remove_ids.length, 0);
    if (!window.confirm(
      `Merge ${groups.length} group${groups.length === 1 ? '' : 's'}?\n\n` +
      `${affected} duplicate record${affected === 1 ? '' : 's'} will be removed.\n` +
      `Tracking, weight, client code, and status will be preserved on the Original.`
    )) return;

    setMerging(true);
    try {
      const r = await axios.post(`${API}/database-a/merge-selected`,
        { groups }, { headers: getAuthHeader() });
      const merged = r.data.groups_merged || 0;
      const removed = r.data.records_removed || 0;
      const editId = r.data.edit_id;
      // Success toast with one-click Undo — sonner supports an action button
      // that calls the revert endpoint. 12s window matches the existing
      // Bulk-Edits pattern.
      toast.success(
        `Duplicate Merge Completed · Groups: ${merged} · Records merged: ${removed + merged} → ${merged}`,
        editId ? {
          duration: 12000,
          action: {
            label: 'Undo',
            onClick: async () => {
              try {
                const rev = await axios.post(
                  `${API}/database-a/edit-history/${editId}/revert`,
                  {}, { headers: getAuthHeader() },
                );
                toast.success(
                  `Merge reverted · ${rev.data?.restored ?? '?'} record${(rev.data?.restored ?? 0) === 1 ? '' : 's'} restored`,
                );
                fetchData(page);
              } catch (revErr) {
                toast.error(revErr?.response?.data?.detail || 'Undo failed');
              }
            },
          },
        } : undefined,
      );
      setDupOpen(false);
      fetchData(page);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  }, [dupData, dupChecked, dupSelectedCount, page, fetchData]);

  const [scanOpen, setScanOpen] = useState(false);
  // Scan → find the parcel in Database A (any date) → open its sheet with tracking edit armed.
  const onScanCode = useCallback(async (code) => {
    const tries = [code, ...(code.length > 14 ? [code.slice(-12)] : [])];
    for (const q of tries) {
      try {
        const res = await axios.get(`${API}/database-a`, { headers: getAuthHeader(), params: { search: q, page: 1, limit: 50 } });
        const recs = res.data.records || [];
        const hit = recs.find(r => (r.track_no || '').toUpperCase() === q) || recs.find(r => code.includes((r.track_no || '').toUpperCase()) && r.track_no);
        if (hit) {
          setSearchQuery(hit.track_no);
          setRecords(recs.filter(r => r.track_no === hit.track_no));
          setMobileRec({ ...hit, _autoEdit: true });
          return true;
        }
      } catch { /* try next */ }
    }
    return false;
  }, []);

  // In-place tracking edit (desktop cell + phone sheet) — mirror the new value onto every parcel row.
  const onTrackingSaved = useCallback((oldT, newT) => {
    setRecords(prev => prev.map(x => x.track_no === oldT ? { ...x, track_no: newT } : x));
    setMobileRec(m => (m && m.track_no === oldT ? { ...m, track_no: newT, _autoEdit: false } : m));
  }, []);

  const fetchMissingWeights = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/database-a/missing-weights`, { headers: getAuthHeader() });
      setMissingWeights(res.data || { missing_invoices: [], total_missing_parcels: 0, total_missing_invoices: 0 });
    } catch {}
  }, []);

  // Split-invoice alerts — invoices whose rows span more than one
  // parcel_date. Powers the subtle auto-heal banner at the top of the
  // panel. Refreshed on mount, after merges, and after any DB refresh.
  const [splitAlerts, setSplitAlerts] = useState({ count: 0, invoices: [] });
  const [splitBannerDismissed, setSplitBannerDismissed] = useState(false);
  const fetchSplitAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/database-a/split-invoice-alerts`,
        { headers: getAuthHeader() });
      setSplitAlerts(res.data || { count: 0, invoices: [] });
    } catch {}
  }, []);

  useEffect(() => { fetchData(1); fetchMissingWeights(); fetchSplitAlerts(); }, []);

  // Refetch when Client / Service filters change (auto-apply, no button press)
  const _initialFiltersMount = useRef(true);
  useEffect(() => {
    if (_initialFiltersMount.current) {
      _initialFiltersMount.current = false;
      return;
    }
    fetchData(1);
  }, [selectedClient, selectedServices]);

  // Listen for refresh events fired from descendant actions (e.g. Revert in History modal)
  // — avoids capturing fetchData in those callbacks which would cause a TDZ error.
  useEffect(() => {
    const onRefresh = () => { fetchData(page); fetchSplitAlerts(); };
    window.addEventListener('database-a-refresh', onRefresh);
    return () => window.removeEventListener('database-a-refresh', onRefresh);
  }, [fetchData, page, fetchSplitAlerts]);

  const downloadExcel = async () => {
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (selectedClient) params.set('client_code', selectedClient);
      if (selectedServices.length > 0) params.set('service', selectedServices.join(','));
      if (searchQuery) params.set('search', searchQuery);
      if (issueFilter) params.set('issues', issueFilter);
      if (trackingStatus) params.set('tracking_status', trackingStatus);
      const res = await axios.get(`${API}/database-a/excel?${params}`, { headers: getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `Database_A${issueFilter ? '_' + issueFilter : ''}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch { toast.error('Download failed'); }
  };

  const handlePrint = () => {
    const w = window.open('', '_blank');
    const rows = records.map(r => `<tr${r.cancelled ? ' style="background:#fce7f3;color:#831843"' : ''}>
      <td>${r.invoice_no}</td><td>${r.client_code}</td><td>${r.track_no}</td>
      <td>${r.product}</td><td>${r.qty}</td><td>${r.customer_name}</td>
      <td>${r.country}</td><td>${r.service}</td><td>${r.parcel_date}</td>
      <td>${r.cancelled ? '<b>CANCELLED</b>' : (r.weight || '')}</td></tr>`).join('');
    w.document.write(`<html><head><title>Database A</title>
      <style>body{font-family:Calibri,sans-serif;padding:20px}table{border-collapse:collapse;width:100%}
      th,td{border:1px solid #ccc;padding:5px 8px;font-size:11px}th{background:#1B4F72;color:#fff}</style>
      </head><body><h2>Database A — ${dateLabel}${issueFilter ? ' (Filtered: ' + issueFilter + ')' : ''}</h2>
      <table><tr><th>Invoice</th><th>Client Code</th><th>Tracking</th><th>2nd Name</th><th>Qty</th>
      <th>Customer Name</th><th>Country</th><th>Service</th><th>OK Parcel DT</th><th>Weight</th></tr>${rows}</table></body></html>`);
    w.document.close(); w.print();
  };

  const handleUpload = async () => {
    if (!plFile) { toast.error('Select a packing list output file'); return; }
    setUploading(true);
    beginProgress();
    try {
      const form = new FormData();
      form.append('packing_list_file', plFile);
      if (bdFile) form.append('bulk_data_file', bdFile);
      if (osFile) form.append('order_sheet_file', osFile);
      if (uploadClientCode) form.append('client_code', uploadClientCode);
      const res = { data: await runUploadJob(`${API}/database-a/upload`, form) };
      toast.success(`Done: ${res.data.inserted} new + ${res.data.updated} overwritten`);
      setShowUpload(false); setPlFile(null); setBdFile(null); setOsFile(null); setUploadClientCode('');
      fetchData(1);
      fetchMissingWeights();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || 'Upload failed'); }
    finally { setUploading(false); endProgress(); }
  };

  // Iter 221 — direct-store corrected sheet uploader.
  const handleCorrectedUpload = async () => {
    if (!ccFile) { toast.error('Select the corrected packing sheet'); return; }
    setCcUploading(true);
    beginProgress();
    try {
      const form = new FormData();
      form.append('corrected_sheet', ccFile);
      if (ccBulk) form.append('bulk_data_file', ccBulk);
      if (ccWeight) form.append('weight_sheet_file', ccWeight);
      if (ccClientCode) form.append('default_client_code', ccClientCode);
      const d = await runUploadJob(`${API}/database-a/upload-corrected`, form);
      const bits = [`${d.inserted} new`, `${d.updated} overwritten`];
      if (d.weight_missing) bits.push(`${d.weight_missing} without weight`);
      toast.success(`Corrected upload: ${bits.join(' · ')}`);
      setShowCorrected(false);
      setCcFile(null); setCcBulk(null); setCcWeight(null); setCcClientCode('');
      fetchData(1);
      fetchMissingWeights();
      fetchCcHistory();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Corrected upload failed';
      if (/Import Packing Book/.test(msg)) {
        toast.info('This is a multi-tab packing book — opening Import Packing Book with a preview');
        setPackBookFile(ccFile); setShowCorrected(false); setShowPackBook(true);
        return;
      }
      toast.error(msg);
    } finally { setCcUploading(false); endProgress(); }
  };

  // Iter 225/226 — shared progress strip used by both upload dialogs.
  // Upload phase shows byte %; once the file lands, the server-side job
  // streams phase + "N of M" row counts.
  const UploadProgress = () => {
    if (!ulPhase) return null;
    const PHASE_LABELS = {
      queued: 'Queued…',
      parsing: 'Reading sheet…',
      snapshotting: 'Loading existing rows…',
      merging: 'Merging',
      writing: 'Writing',
    };
    const job = ulJob || {};
    let main;
    if (ulPhase === 'upload') {
      main = `Uploading… ${ulPct}%`;
    } else if (job.total && (job.phase === 'merging' || job.phase === 'writing')) {
      main = `${PHASE_LABELS[job.phase]} ${job.processed} of ${job.total} rows…`;
    } else {
      main = PHASE_LABELS[job.phase] || 'Processing rows…';
    }
    const pct = ulPhase === 'upload'
      ? ulPct
      : (job.total ? Math.min(100, Math.round(((job.processed || 0) / job.total) * 100)) : 100);
    return (
      <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/70 p-2.5"
           data-testid="dba-upload-progress">
        <div className="flex items-center justify-between text-[11px] font-semibold text-indigo-800 mb-1">
          <span data-testid="dba-upload-stage">{main}</span>
          <span data-testid="dba-upload-elapsed">{ulElapsed.toFixed(1)}s</span>
        </div>
        <div className="h-1.5 bg-indigo-100 rounded overflow-hidden">
          <div className={`h-full bg-indigo-500 ${ulPhase === 'processing' && !job.total ? 'animate-pulse' : ''}`}
               style={{ width: `${pct}%` }} />
        </div>
        {ulPhase === 'processing' && (
          <p className="text-[10px] text-indigo-700/80 mt-1">
            File received — server is de-duplicating and merging rows.
            {job.total ? ` ${job.processed || 0} / ${job.total} done.` : ' Large sheets can take up to a minute.'}
          </p>
        )}
      </div>
    );
  };

  // Iter 223 — blank weight-sheet template matching
  // extract_weights_from_weight_sheet: header row "Tracking" + "Weight (gm)".
  const downloadWeightTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([["Tracking", "Weight (gm)"]]);
    ws['!cols'] = [{ wch: 22 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Weights');
    XLSX.writeFile(wb, 'weight-sheet-template.xlsx');
    toast.success('Blank weight sheet downloaded');
  };

  // Iter 223b — blank corrected-sheet template with all 10 columns in the
  // exact header order the upload parser expects.
  const downloadCorrectedTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([[
      'OK PARCEL DT', 'Invoice No', 'Customer Name', '2ND NAME', 'QTY',
      'Service', 'Country', 'Tracking', 'Weight (gm)', 'Client Code',
    ]]);
    ws['!cols'] = [{ wch: 14 }, { wch: 12 }, { wch: 24 }, { wch: 24 },
                   { wch: 6 }, { wch: 10 }, { wch: 16 }, { wch: 20 },
                   { wch: 12 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Corrected');
    XLSX.writeFile(wb, 'corrected-packing-sheet-template.xlsx');
    toast.success('Blank corrected packing sheet downloaded');
  };

  const undoCorrectedUpload = async (uploadId) => {
    if (!window.confirm('Roll this upload back? Rows it inserted will be deleted and rows it changed will be restored to their previous values.')) return;
    setCcUndoBusy(uploadId);
    try {
      const r = await axios.post(
        `${API}/database-a/corrected-uploads/${uploadId}/undo`, {},
        { headers: getAuthHeader() });
      toast.success(`Undone: ${r.data.deleted} deleted · ${r.data.restored} restored`);
      fetchCcHistory();
      fetchData(1);
      fetchMissingWeights();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Undo failed');
    } finally { setCcUndoBusy(""); }
  };

  const handleBackfill = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/database-a/backfill`, {}, { headers: getAuthHeader() });
      toast.success(`Backfill: ${res.data.records_inserted} records, ${res.data.weights_updated} weights`);
      fetchData(1);
    } catch { toast.error('Backfill failed'); }
    finally { setLoading(false); }
  };

  const handleFixWeights = async () => {
    setLoading(true);
    toast.info('Scanning all bulk label + packing list sources — please wait…');
    try {
      const res = await axios.post(`${API}/database-a/fix-weights`, {}, { headers: getAuthHeader() });
      const d = res.data;
      toast.success(
        `Updated ${d.weights_updated} weights across ${d.parcels_weighed} parcels (scanned ${d.bulk_labels_scanned} bulk labels + ${d.packing_lists_scanned} packing lists).`,
        { duration: 6000 }
      );
      fetchData(1);
      fetchStats();
      fetchMissingWeights();
      if (d.still_without_weight > 0 && Array.isArray(d.still_missing_by_invoice) && d.still_missing_by_invoice.length > 0) {
        toast.warning(
          `${d.still_without_weight} rows still without weight — see "Missing Weight Sources" card below to upload the Bulk Data Sheet for each invoice.`,
          { duration: 10000 }
        );
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Fix Weights failed'); }
    finally { setLoading(false); }
  };

  const handlePerInvoiceBulkDataUpload = async (invoiceNo, file) => {
    if (!file) return;
    setPerInvUploading(invoiceNo);
    try {
      const form = new FormData();
      form.append('bulk_data_file', file);
      const res = await axios.post(`${API}/database-a/upload-bulk-data`, form, { headers: getAuthHeader() });
      const d = res.data;
      if (d.weights_updated > 0) {
        toast.success(`${invoiceNo}: Filled ${d.weights_updated} weights across ${d.parcels_weighed} parcels.`);
      } else {
        toast.warning(
          `No matching tracking numbers found for ${invoiceNo} in this file. ` +
          `The file had ${d.tracking_numbers_in_file} tracking numbers${d.invoice_detected ? ' (invoice detected: ' + d.invoice_detected + ')' : ''}.`,
          { duration: 8000 }
        );
      }
      await Promise.all([fetchData(1), fetchStats(), fetchMissingWeights()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setPerInvUploading('');
    }
  };

  const applyPreset = (p) => { const [f, t] = p.fn(); setDateFrom(f); setDateTo(t); setShowDatePicker(false); };

  // Prefer backend-calculated scope counts so badges always match the currently
  // applied date / client / search filters (parcel-aware for zero-weight).
  const totalInScope = (issueCounts.total !== undefined) ? issueCounts.total : total;
  const totalErrors = (issueCounts.all_errors !== undefined)
    ? issueCounts.all_errors
    : ((issueCounts.zero_qty || 0) + (issueCounts.zero_weight || 0) + (issueCounts.blank_service || 0) + (issueCounts.blank_date || 0) + (issueCounts.blank_invoice || 0));

  const [activeSubTab, setActiveSubTab] = useState('records'); // 'records' | 'pending'

  return (
    <div className="flex flex-col md:h-[calc(100vh-80px)] p-2 sm:p-6" data-testid="database-a-panel">
      {/* Sub-tab toggle: main records view ↔ Pending Parcels report. */}
      <div className="flex items-center gap-1 mb-2" data-testid="dba-subtab-toggle">
        <button
          type="button"
          onClick={() => setActiveSubTab('records')}
          className={`text-xs px-3 py-1.5 rounded-t border-b-2 transition ${
            activeSubTab === 'records'
              ? 'bg-white border-indigo-600 text-indigo-700 font-semibold'
              : 'bg-slate-100 border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="dba-subtab-records"
        >
          Records
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('pending')}
          className={`text-xs px-3 py-1.5 rounded-t border-b-2 transition ${
            activeSubTab === 'pending'
              ? 'bg-white border-indigo-600 text-indigo-700 font-semibold'
              : 'bg-slate-100 border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="dba-subtab-pending"
        >
          Pending Parcels
        </button>
      </div>

      {activeSubTab === 'pending' ? (
        <div className="flex-1 overflow-auto">
          <PendingParcelsPanel />
        </div>
      ) : (
      <>
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 px-3 sm:px-4 py-2 sm:py-3 mb-2 sm:mb-3">
        <div className="flex items-center justify-between gap-2 sm:gap-3 flex-wrap">
          <div className="flex items-center gap-2 sm:gap-3">
            <Database className="w-5 h-5 text-indigo-600" />
            <div>
              <h2 className="text-sm sm:text-base font-semibold text-slate-900">Database A</h2>
              {stats && (
                <p className="text-[10px] sm:text-xs text-slate-400 hidden sm:block">
                  {stats.total_records} records | {stats.unique_invoices} invoices | {stats.unique_customers} customers | {stats.with_weight || 0} with weight
                </p>
              )}
              {stats && stats.temp_emi_parcels > 0 && (
                <button
                  type="button"
                  onClick={() => setTrackingStatus(prev => prev === 'temp_emi' ? null : 'temp_emi')}
                  className={`mt-1 inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full border transition ${
                    trackingStatus === 'temp_emi'
                      ? 'bg-amber-500 border-amber-500 text-white shadow-sm'
                      : 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                  }`}
                  title={trackingStatus === 'temp_emi'
                    ? 'Showing only temp EMI parcels — click to clear'
                    : 'Show only parcels still on auto-generated EMI trackings'}
                  data-testid="dba-temp-emi-chip"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-inner" />
                  <strong>{stats.temp_emi_parcels}</strong>&nbsp;pending&nbsp;EMI
                  {trackingStatus === 'temp_emi' && (
                    <span className="ml-1 opacity-80">· ✕</span>
                  )}
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap w-full sm:w-auto">
            <div className="relative">
              <button onClick={() => setShowDatePicker(!showDatePicker)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 max-w-[280px]"
                data-testid="dba-date-picker">
                <CalendarRange className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                <span className="truncate">{dateLabel}</span>
                <ChevronDown className="w-3 h-3 flex-shrink-0" />
              </button>
              {showDatePicker && (
                <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 p-3 z-30 min-w-[240px]">
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {PERIOD_PRESETS.map(p => (
                      <button key={p.id} onClick={() => applyPreset(p)} data-testid={`dba-preset-${p.id}`}
                        className="px-2.5 py-1 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                      >{p.label}</button>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs text-slate-500 font-medium">From</label>
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                    <label className="block text-xs text-slate-500 font-medium">To</label>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                    <Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white mt-1"
                      onClick={() => { setShowDatePicker(false); fetchData(1); }}>Apply</Button>
                  </div>
                </div>
              )}
            </div>
            <MobileCollapse isMobile={isMobile} label="Filters" kind="filters" testid="dba-mobile-filters" badge={(selectedClient ? 1 : 0) + (selectedServices.length ? 1 : 0)}>
            {clientCodes.length > 0 && (
              <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)}
                className="px-2 sm:px-3 py-2 border border-slate-300 rounded-lg text-xs sm:text-sm bg-white" data-testid="dba-client-filter">
                <option value="">All Clients</option>
                {clientCodes.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            {servicesList.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowServicePicker(v => !v)}
                  className="px-2 sm:px-3 py-2 border border-slate-300 rounded-lg text-xs sm:text-sm bg-white flex items-center gap-1 hover:bg-slate-50"
                  data-testid="dba-service-filter"
                  title="Filter parcels by one or more courier services">
                  <span className="font-medium">
                    {selectedServices.length === 0
                      ? 'All Services'
                      : selectedServices.length === 1
                        ? selectedServices[0]
                        : `${selectedServices.length} services`}
                  </span>
                  <ChevronDown className="w-3 h-3 flex-shrink-0" />
                </button>
                {showServicePicker && (
                  <>
                    {/* click-outside catcher */}
                    <div className="fixed inset-0 z-20"
                         onClick={() => setShowServicePicker(false)} />
                    <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-30 min-w-[180px] max-h-[280px] overflow-y-auto"
                         data-testid="dba-service-picker">
                      <button
                        type="button"
                        onClick={() => setSelectedServices([])}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 border-b border-slate-100"
                        data-testid="dba-service-clear">
                        <span className={selectedServices.length === 0 ? 'font-semibold text-indigo-700' : 'text-slate-600'}>
                          {selectedServices.length === 0 ? '✓ ' : ''}All Services
                        </span>
                      </button>
                      {servicesList.map(s => {
                        const checked = selectedServices.includes(s);
                        return (
                          <label
                            key={s}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-indigo-50 cursor-pointer"
                            data-testid={`dba-service-opt-${s}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                setSelectedServices(prev =>
                                  prev.includes(s)
                                    ? prev.filter(x => x !== s)
                                    : [...prev, s]
                                )
                              }
                              className="cursor-pointer" />
                            <span className={checked ? 'font-semibold text-indigo-700' : 'text-slate-700'}>
                              {s}
                            </span>
                          </label>
                        );
                      })}
                      {selectedServices.length > 0 && (
                        <div className="border-t border-slate-100 mt-1 pt-1 px-2 pb-1">
                          <button
                            type="button"
                            onClick={() => setShowServicePicker(false)}
                            className="w-full px-2 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium"
                            data-testid="dba-service-picker-done">
                            Done ({selectedServices.length})
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            </MobileCollapse>
            <div className="relative flex-1 sm:flex-none min-w-[130px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchData(1)} placeholder="Search name, tracking..."
                className="pl-8 pr-3 py-2 border border-slate-300 rounded-lg text-sm w-full sm:w-44" data-testid="dba-search" />
            </div>
            {isMobile && (
              <Button onClick={() => setScanOpen(true)} variant="outline" size="sm" className="border-indigo-300 text-indigo-700" data-testid="dba-scan-btn">
                <ScanLine className="w-4 h-4 mr-1" /> Scan
              </Button>
            )}
            <Button onClick={() => fetchData(1)} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm" data-testid="dba-search-btn">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
            <div className="w-px h-6 bg-slate-200 hidden sm:block" />
            <MobileCollapse isMobile={isMobile} label="Menu" testid="dba-mobile-menu">
            <Button variant="outline" size="sm" onClick={() => setShowUpload(true)} data-testid="dba-upload-btn">
              <Upload className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Upload</span>
            </Button>
            <Button variant="outline" size="sm" onClick={downloadExcel} data-testid="dba-excel-btn">
              <Download className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Excel</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="dba-print-btn">
              <Printer className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Print</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleFixWeights}
              disabled={loading}
              className="border-amber-400 text-amber-700 hover:bg-amber-50"
              data-testid="dba-fix-weights-btn"
              title="Rescan all bulk label + packing list history files and fill missing weights"
            >
              {loading ? <RefreshCw className="w-4 h-4 sm:mr-1 animate-spin" /> : <span className="sm:mr-1">⚖︎</span>}
              <span className="hidden sm:inline">Fix Weights</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => mergeDuplicates('default')}
              disabled={merging}
              className="border-violet-400 text-violet-700 hover:bg-violet-50"
              data-testid="dba-merge-duplicates-btn"
              title="Find rows with the same tracking + product and merge them into one (keeps the row with weight)"
            >
              {merging
                ? <RefreshCw className="w-4 h-4 sm:mr-1 animate-spin" />
                : <span className="sm:mr-1">⇉</span>}
              <span className="hidden sm:inline">Merge Duplicates</span>
            </Button>
            {selectedRows.size > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={deleteSelected}
                disabled={deleting}
                className="border-red-500 text-red-600 hover:bg-red-50 font-semibold"
                data-testid="dba-delete-selected-btn"
                title="Delete the rows selected by clicking their # number"
              >
                {deleting
                  ? <RefreshCw className="w-4 h-4 sm:mr-1 animate-spin" />
                  : <Trash2 className="w-4 h-4 sm:mr-1" />}
                <span className="hidden sm:inline">Delete Selected</span> ({selectedRows.size})
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowMissingCard(s => !s); if (!showMissingCard) fetchMissingWeights(); }}
              className={`border-amber-400 ${showMissingCard ? 'bg-amber-100 text-amber-900' : 'text-amber-700 hover:bg-amber-50'} relative`}
              data-testid="dba-missing-weights-btn"
              title="Show / hide the per-invoice Bulk Data Sheet upload card"
            >
              <AlertTriangle className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Missing Weight Sources</span>
              {missingWeights.total_missing_invoices > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none">
                  {missingWeights.total_missing_invoices}
                </span>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setHistoryOpen(true); fetchHistory(); }}
              className="border-indigo-400 text-indigo-700 hover:bg-indigo-50"
              data-testid="dba-history-btn"
              title="See who edited which Database A cell and when"
            >
              <History className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">History</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={openBulkHistoryDialog}
              className="border-amber-400 text-amber-700 hover:bg-amber-50"
              data-testid="dba-bulk-history-btn"
              title="See recent bulk fixes (Emirates sync, blank-code fills) and revert them in one click"
            >
              <History className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Bulk Edits</span>
            </Button>
            <EmiratesTrackingUpdate testId="dba" onApplied={() => fetchData()} />
            {user?.role === 'admin' && (
              <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setAdminPurgePreview(null); setAdminPurgeOpen(true); fetchPurgeBatches(); fetchPurgePwdMeta(); }}
                className="border-red-500 text-red-700 hover:bg-red-50"
                data-testid="dba-admin-purge-btn"
                title="Admin only: bulk delete by date / client / service (password protected)"
              >
                <Trash2 className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Admin Purge</span>
              </Button>
              {/* Iter 221 — direct-store upload of admin-corrected data */}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowCorrected(true)}
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                data-testid="dba-corrected-upload-btn"
                title="Admin only: upload a manually-corrected packing sheet (weights inline, direct store)"
              >
                <Upload className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Upload Corrected</span>
              </Button>
              {user?.role === 'admin' && (
                <Button size="sm" variant="outline" onClick={() => { setPackBookFile(null); setShowPackBook(true); }}
                  className="border-teal-500 text-teal-700 hover:bg-teal-50" data-testid="dba-packbook-btn"
                  title="Import a multi-tab packing list book with preview (Skip / Overwrite / Replace)">
                  <Upload className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Import Packing Book</span>
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowWeightSheet(true)}
                className="border-blue-500 text-blue-700 hover:bg-blue-50"
                data-testid="dba-weight-sheet-btn"
                title="Admin only: upload the monthly multi-tab packing-list workbook to fill weights across every service in one go"
              >
                <Package className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Apply Weight Sheet</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { fetchCcHistory(); setCcHistoryOpen(true); }}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="dba-corrected-history-btn"
                title="Admin only: recent corrected uploads with one-click undo"
              >
                <History className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Corrected History</span>
              </Button>
              </>
            )}
            </MobileCollapse>
          </div>
        </div>

        {/* Issue Filter Pills */}
        <MobileCollapse isMobile={isMobile} label="Issues" kind="filters" testid="dba-mobile-issues" badge={issueFilter ? 1 : 0}>
        <div className="flex items-center gap-1.5 mt-3 flex-wrap" data-testid="dba-issue-filters">
          {ISSUE_FILTERS.map(f => {
            const count = f.id === 'all_errors' ? totalErrors : f.id ? (issueCounts[f.id] || 0) : totalInScope;
            const isActive = issueFilter === f.id;
            return (
              <button key={f.id} onClick={() => { setIssueFilter(f.id); setTimeout(() => fetchData(1), 50); }}
                data-testid={`dba-filter-${f.id || 'all'}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  isActive
                    ? (f.color || 'bg-indigo-100 text-indigo-700 border-indigo-300') + ' ring-2 ring-offset-1 ring-indigo-400'
                    : f.id ? 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50' : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
                }`}
              >
                {f.id && f.id !== '' && (f.id === 'cancelled'
                  ? <Ban className="w-3 h-3" />
                  : f.id === 'parcels'
                  ? <Package className="w-3 h-3" />
                  : <AlertTriangle className="w-3 h-3" />)}
                {f.label}
                {count > 0 && <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  isActive ? 'bg-white/60' : f.id ? 'bg-slate-100' : 'bg-white'
                }`}>{count}</span>}
              </button>
            );
          })}
        </div>
        </MobileCollapse>
      </div>

      {/* Split-Invoice / Mixed-Tracking auto-heal banner. Flags:
          (a) invoices whose rows span multiple parcel_dates, and
          (b) trackings with two or more DIFFERENT invoices under the
              same customer (e.g. regenerated packing list with new
              invoice + new product but the old row wasn't removed).
          One click opens the matching merge tab. */}
      {splitAlerts.count > 0 && !splitBannerDismissed && (
        <div
          className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-lg shadow-sm px-3 py-2 mb-3 flex items-center justify-between gap-3"
          data-testid="dba-split-invoice-banner"
        >
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0" />
            <div className="min-w-0">
              <div className="text-[12px] font-semibold text-amber-900 truncate">
                {splitAlerts.count} parcel{splitAlerts.count === 1 ? '' : 's'} need review
                {(splitAlerts.split_date_count > 0 || splitAlerts.mixed_tracking_count > 0) && (
                  <span className="text-[10.5px] font-normal text-amber-800">
                    {' '}({splitAlerts.split_date_count > 0 && `${splitAlerts.split_date_count} split-date`}
                    {(splitAlerts.split_date_count > 0 && splitAlerts.mixed_tracking_count > 0) && ' · '}
                    {splitAlerts.mixed_tracking_count > 0 && `${splitAlerts.mixed_tracking_count} same-tracking / diff invoice`})
                  </span>
                )}
              </div>
              <div className="text-[10.5px] text-amber-800 truncate" title={splitAlerts.invoices.map(i => {
                const inv = Array.isArray(i.invoices) ? i.invoices.join(' / ') : i.invoice_no;
                return `[${i.reason || 'split'}] ${inv} — ${(i.dates || []).join(', ') || i.customer_name || ''}`;
              }).join('\n')}>
                {splitAlerts.invoices.slice(0, 4).map(i => i.invoice_no || (i.invoices || []).join('/')).join(', ')}
                {splitAlerts.invoices.length > 4 ? ` … +${splitAlerts.invoices.length - 4} more` : ''}
                &nbsp;·&nbsp;Same invoice + tracking, or same tracking with different invoices — needs merge
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {splitAlerts.split_date_count > 0 && (
              <Button
                size="sm"
                onClick={() => mergeDuplicates('cross_date')}
                className="bg-amber-600 hover:bg-amber-700 text-white h-7 px-2.5 text-[11px]"
                data-testid="dba-split-invoice-review-btn"
                title="Open Cross-date merge tab"
              >
                <Wand2 className="w-3 h-3 mr-1" />
                Cross-date
              </Button>
            )}
            {splitAlerts.mixed_tracking_count > 0 && (
              <Button
                size="sm"
                onClick={() => mergeDuplicates('same_tracking')}
                className="bg-amber-600 hover:bg-amber-700 text-white h-7 px-2.5 text-[11px]"
                data-testid="dba-split-invoice-same-tracking-btn"
                title="Open Same-tracking merge tab"
              >
                <Wand2 className="w-3 h-3 mr-1" />
                Same-tracking
              </Button>
            )}
            <button
              type="button"
              onClick={() => setSplitBannerDismissed(true)}
              className="text-amber-700 hover:text-amber-900 text-lg leading-none px-1"
              data-testid="dba-split-invoice-dismiss"
              title="Hide until next refresh"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Blank-Client-Code — one-click fix card, shown only when this filter is active */}
      {issueFilter === 'blank_client_code' && (issueCounts.blank_client_code || 0) > 0 && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-xl shadow-sm px-4 py-3 mb-3"
             data-testid="dba-fix-blank-client-code-card">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-yellow-700" />
              <div>
                <div className="text-sm font-semibold text-yellow-900">
                  {issueCounts.blank_client_code} row{issueCounts.blank_client_code === 1 ? '' : 's'} with blank Client Code
                </div>
                <div className="text-[11px] text-yellow-800">
                  Pick a code per invoice — most invoices already have a client code on other rows, so a suggestion appears automatically.
                </div>
              </div>
            </div>
            <Button size="sm" onClick={openFixCcDialog}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white h-8 px-3 text-xs"
                    data-testid="dba-open-fix-cc-dialog">
              <Wand2 className="w-3 h-3 mr-1" />
              Fix Blank Client Codes
            </Button>
          </div>
        </div>
      )}

      {/* Blank-Date — one-click fix card, shown only when this filter is active */}
      {issueFilter === 'blank_date' && (issueCounts.blank_date || 0) > 0 && (
        <div className="bg-pink-50 border border-pink-300 rounded-xl shadow-sm px-4 py-3 mb-3"
             data-testid="dba-fix-blank-date-card">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-pink-700" />
              <div>
                <div className="text-sm font-semibold text-pink-900">
                  {issueCounts.blank_date} row{issueCounts.blank_date === 1 ? '' : 's'} with blank Parcel Date
                </div>
                <div className="text-[11px] text-pink-800">
                  Dates from sibling rows of the same invoice (or same tracking) are surfaced as one-click chips.
                </div>
              </div>
            </div>
            <Button size="sm" onClick={openFixDateDialog}
                    className="bg-pink-600 hover:bg-pink-700 text-white h-8 px-3 text-xs"
                    data-testid="dba-open-fix-date-dialog">
              <Wand2 className="w-3 h-3 mr-1" />
              Fix Blank Dates
            </Button>
          </div>
        </div>
      )}

      {/* Missing Weight Sources — per-invoice upload card */}
      {missingWeights.total_missing_invoices > 0 && showMissingCard && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl shadow-sm px-4 py-3 mb-3" data-testid="dba-missing-weights-card">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-semibold text-amber-900">
                Missing Weight Sources
                <span className="ml-2 text-xs font-normal text-amber-700">
                  {missingWeights.total_missing_parcels} parcels across {missingWeights.total_missing_invoices} invoices
                </span>
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-amber-700 hidden sm:inline">Upload each invoice's Bulk Data Sheet below to fill weights.</span>
              <button
                onClick={() => setShowMissingCard(false)}
                className="text-amber-600 hover:text-amber-800"
                data-testid="dba-missing-weights-close"
                title="Hide for this session"
              ><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-56 overflow-y-auto">
            {missingWeights.missing_invoices.map(inv => (
              <div
                key={inv.invoice_no}
                className="flex items-center justify-between gap-2 bg-white border border-amber-200 rounded-lg px-3 py-2"
                data-testid={`dba-missing-${inv.invoice_no}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-xs font-semibold text-slate-900">{inv.invoice_no}</span>
                    <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                      {inv.parcels_missing_weight} parcel{inv.parcels_missing_weight > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {inv.client_codes.join(', ') || '—'}
                    {inv.parcel_dates.length > 0 && ` · ${inv.parcel_dates[0]}`}
                  </div>
                </div>
                <label
                  className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium cursor-pointer whitespace-nowrap ${
                    perInvUploading === inv.invoice_no
                      ? 'bg-amber-200 text-amber-800 cursor-wait'
                      : 'bg-amber-500 hover:bg-amber-600 text-white'
                  }`}
                  data-testid={`dba-upload-${inv.invoice_no}`}
                >
                  {perInvUploading === inv.invoice_no
                    ? <><RefreshCw className="w-3 h-3 animate-spin" />Uploading…</>
                    : <><Upload className="w-3 h-3" />Bulk Data</>}
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    disabled={!!perInvUploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handlePerInvoiceBulkDataUpload(inv.invoice_no, f);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowUpload(false)}>
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-6 w-[500px] max-w-[95vw] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Upload className="w-5 h-5 text-indigo-600" />Upload & Fix Data
              </h3>
              <button onClick={() => setShowUpload(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-blue-700 font-medium flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" />
                Upload corrected files — matching tracking numbers will be overwritten automatically
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Packing List Output <span className="text-red-500">*</span></label>
                <p className="text-xs text-slate-400 mb-2">Track No, Product, Qty, Customer, Country, Service — matching tracking rows get overwritten</p>
                <input type="file" accept=".xlsx,.xls" onChange={e => setPlFile(e.target.files[0])}
                  className="w-full text-sm border border-slate-300 rounded-lg p-2" data-testid="dba-pl-file" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Bulk Data Sheet <span className="text-slate-400">(for Weight)</span></label>
                <p className="text-xs text-slate-400 mb-2">ARTICLE NUMBER + PHYSICAL WEIGHT columns — weights matched by tracking</p>
                <input type="file" accept=".xlsx,.xls" onChange={e => setBdFile(e.target.files[0])}
                  className="w-full text-sm border border-slate-300 rounded-lg p-2" data-testid="dba-bd-file" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Order Sheet <span className="text-slate-400">(for Client Code)</span></label>
                <p className="text-xs text-slate-400 mb-2">Original order sheet — Client Code extracted by matching tracking</p>
                <input type="file" accept=".xlsx,.xls" onChange={e => setOsFile(e.target.files[0])}
                  className="w-full text-sm border border-slate-300 rounded-lg p-2" data-testid="dba-os-file" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Client Code <span className="text-slate-400">(optional override)</span></label>
                <input type="text" value={uploadClientCode} onChange={e => setUploadClientCode(e.target.value)}
                  placeholder="e.g., EXPO, ESR" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="dba-client-input" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleUpload} disabled={uploading || !plFile} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="dba-upload-submit">
                  {uploading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                  {uploading ? 'Processing...' : 'Upload & Overwrite'}
                </Button>
                <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
              </div>
              <UploadProgress />
            </div>
          </div>
        </div>
      )}

      {/* Iter 221 — Corrected data direct-store uploader (post-purge scenario) */}
      <PackingBookImportDialog open={showPackBook} onOpenChange={setShowPackBook} initialFile={packBookFile}
        onDone={() => { fetchData(1); fetchMissingWeights(); fetchCcHistory(); }} />
      {showCorrected && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCorrected(false)}
             data-testid="dba-corrected-dialog">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-6 w-[520px] max-w-[95vw] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Upload className="w-5 h-5 text-emerald-600" />Upload Corrected Data
              </h3>
              <button onClick={() => setShowCorrected(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <button type="button" onClick={() => { setShowCorrected(false); setPackBookFile(null); setShowPackBook(true); }}
              className="w-full mb-3 rounded-lg border border-teal-300 bg-teal-50 p-2 text-left text-xs text-teal-800 hover:bg-teal-100" data-testid="dba-cc-open-packbook">
              Have a multi-tab packing list book (one tab per day/service, e.g. AUGUST PACKING SHEET)? <b>Use Import Packing Book →</b> (preview, pick tabs, Skip / Overwrite / Replace)
            </button>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-emerald-800 font-medium">
                Direct-store path for admin-corrected packing sheets. Weights inline are used as-is;
                the bulk data / weight-only sheets are only consulted for rows missing a weight.
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Corrected Packing Sheet <span className="text-red-500">*</span></label>
                <p className="text-xs text-slate-400 mb-2">
                  Excel or CSV. Clean template: PARCEL DT, Invoice No, Customer Name, 2ND NAME, QTY, Service, Country, Tracking, Weight (gm), Client Code.
                  Raw register exports also work — TIME / Notes / BACKEND / DELIVERED / NEW TRACKING style extra columns are skipped automatically, CODE is read as Client Code, 2ND BAND NAME as product (Medicines used when blank), and courier words in the Tracking column (cancelled rows) are ignored.
                </p>
                <input type="file" accept=".xlsx,.xls,.csv" onChange={e => setCcFile(e.target.files[0])}
                  className="w-full text-sm border border-slate-300 rounded-lg p-2" data-testid="dba-cc-file" />
                <button type="button" onClick={downloadCorrectedTemplate}
                  className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 hover:underline"
                  data-testid="dba-cc-corrected-template"
                  title="Download a blank corrected packing sheet with all 10 columns pre-labelled">
                  <Download className="w-3 h-3" /> Download blank corrected sheet template
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Bulk Data Sheet <span className="text-slate-400">(fallback for missing weights)</span></label>
                <input type="file" accept=".xlsx,.xls" onChange={e => setCcBulk(e.target.files[0])}
                  className="w-full text-sm border border-slate-300 rounded-lg p-2" data-testid="dba-cc-bulk-file" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Weight Sheet <span className="text-slate-400">(Emirates / DHL manual capture)</span></label>
                <p className="text-xs text-slate-400 mb-2">Simple 2-column sheet: Tracking + Weight — used only when the two above still don't have a weight</p>
                <input type="file" accept=".xlsx,.xls" onChange={e => setCcWeight(e.target.files[0])}
                  className="w-full text-sm border border-slate-300 rounded-lg p-2" data-testid="dba-cc-weight-file" />
                <button type="button" onClick={downloadWeightTemplate}
                  className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 hover:underline"
                  data-testid="dba-cc-weight-template"
                  title="Download a blank weight sheet in the exact format this field expects">
                  <Download className="w-3 h-3" /> Download blank weight sheet template
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Default Client Code <span className="text-slate-400">(optional)</span></label>
                <input type="text" value={ccClientCode} onChange={e => setCcClientCode(e.target.value)}
                  placeholder="Used only when the sheet row has no client code"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="dba-cc-client" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleCorrectedUpload} disabled={ccUploading || !ccFile}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="dba-cc-submit">
                  {ccUploading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                  {ccUploading ? 'Storing…' : 'Store Corrected Data'}
                </Button>
                <Button variant="outline" onClick={() => setShowCorrected(false)}>Cancel</Button>
              </div>
              <UploadProgress />
            </div>
          </div>
        </div>
      )}

      {/* Iter 252 — Multi-tab packing-list weight sheet apply */}
      {showWeightSheet && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
             onClick={() => !wsBusy && setShowWeightSheet(false)}
             data-testid="dba-weight-sheet-dialog">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-6 w-[640px] max-w-[95vw] max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-600" /> Apply Weight Sheet
              </h3>
              <button onClick={() => !wsBusy && setShowWeightSheet(false)}
                      className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Upload the monthly packing-list workbook (one tab per date+service — EMS / ITPS / EMIRATES / DHL / FEDEX …).
              Every tab is scanned, weights are picked from the WEIGHT column, and Database A rows are matched by Tracking first
              (or Customer Name + Parcel Date as fallback for DHL-style tabs without tracking). Dimension lines (HEIGHT / WIDTH / LENGTH) are skipped.
            </p>
            <input type="file" accept=".xlsx,.xls"
                   onChange={e => { setWsFile(e.target.files[0]); setWsResult(null); setWsDiffTab(null); }}
                   disabled={wsBusy}
                   className="w-full text-sm border border-slate-300 rounded-lg p-2 disabled:bg-slate-50"
                   data-testid="dba-ws-file" />
            <label className="flex items-center gap-2 mt-3 text-sm text-slate-700">
              <input type="checkbox" checked={wsOverwrite}
                     onChange={e => setWsOverwrite(e.target.checked)}
                     disabled={wsBusy}
                     data-testid="dba-ws-overwrite" />
              Overwrite existing weights (default: fill only blank/zero)
            </label>
            {wsResult && (
              <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs"
                   data-testid="dba-ws-result">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <div><b>Parcels in sheet:</b> {wsResult.parcels}</div>
                  <div><b>Rows updated:</b> <span className="text-emerald-700 font-semibold">{wsResult.updated_rows}</span></div>
                  <div><b>Invoices filled:</b> {wsResult.invoices_filled ?? 0}</div>
                  <div><b>Countries filled:</b> {wsResult.countries_filled ?? 0}</div>
                  <div><b>Unmatched parcels:</b> {wsResult.unmatched_count}</div>
                  <div><b>Data differences:</b> <span className={wsResult.diff_count ? 'text-amber-700 font-semibold' : ''}>{wsResult.diff_count ?? 0}</span></div>
                </div>
                {(wsResult.diff_count > 0 || wsResult.unmatched_count > 0) && (
                  <div className="flex gap-2 mt-3">
                    {wsResult.diff_count > 0 && (
                      <Button size="sm" variant="outline"
                              onClick={() => setWsDiffTab('diffs')}
                              className={`border-amber-400 text-amber-700 hover:bg-amber-50 ${wsDiffTab === 'diffs' ? 'bg-amber-50' : ''}`}
                              data-testid="dba-ws-show-diffs">
                        View Corrected Data ({wsResult.diff_count})
                      </Button>
                    )}
                    {wsResult.unmatched_count > 0 && (
                      <Button size="sm" variant="outline"
                              onClick={() => setWsDiffTab('unmatched')}
                              className={`border-slate-400 text-slate-700 hover:bg-slate-50 ${wsDiffTab === 'unmatched' ? 'bg-slate-50' : ''}`}
                              data-testid="dba-ws-show-unmatched">
                        Unmatched Parcels ({wsResult.unmatched_count})
                      </Button>
                    )}
                    {wsDiffTab && (
                      <Button size="sm" variant="ghost" onClick={() => setWsDiffTab(null)}
                              className="text-slate-500">Hide</Button>
                    )}
                  </div>
                )}
                {wsDiffTab === 'diffs' && (
                  <div className="mt-3 max-h-[300px] overflow-auto border rounded bg-white" data-testid="dba-ws-diffs-list">
                    <table className="w-full text-[11px]">
                      <thead className="bg-slate-100 text-slate-600 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-1">Kind</th>
                          <th className="text-left px-2 py-1">Tracking</th>
                          <th className="text-left px-2 py-1">Customer</th>
                          <th className="text-left px-2 py-1">Sheet</th>
                          <th className="text-left px-2 py-1">DB</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wsResult.diffs.map((d, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-2 py-1 whitespace-nowrap">
                              <span className={
                                d.kind === 'qty_differs' ? 'text-amber-700' :
                                d.kind === 'country_differs' ? 'text-blue-700' :
                                d.kind === 'product_missing_in_db' ? 'text-red-700' :
                                'text-slate-600'}>
                                {d.kind.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className="px-2 py-1 font-mono">{d.track_no || '—'}</td>
                            <td className="px-2 py-1">{d.customer_name || '—'}</td>
                            <td className="px-2 py-1">
                              {d.sheet_product && <div>{d.sheet_product}</div>}
                              {d.sheet_qty != null && <div className="text-slate-500">qty {d.sheet_qty}</div>}
                              {d.sheet_country && <div className="text-slate-500">{d.sheet_country}</div>}
                            </td>
                            <td className="px-2 py-1">
                              {d.db_product && <div>{d.db_product}</div>}
                              {d.db_qty != null && <div className="text-slate-500">qty {d.db_qty}</div>}
                              {d.db_country && <div className="text-slate-500">{d.db_country}</div>}
                              {!d.db_product && !d.db_qty && !d.db_country && <span className="text-slate-400">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {wsResult.diff_count > wsResult.diffs.length && (
                      <div className="p-2 text-slate-500 text-center">
                        showing first {wsResult.diffs.length} of {wsResult.diff_count}
                      </div>
                    )}
                  </div>
                )}
                {wsDiffTab === 'unmatched' && (
                  <ul className="mt-3 ml-4 list-disc max-h-[300px] overflow-auto text-slate-700"
                      data-testid="dba-ws-unmatched-list">
                    {wsResult.unmatched_parcels.map((u, i) => (
                      <li key={i}>
                        {u.track_no || '(no tracking)'} · {u.customer_name} · {u.parcel_date}
                        <span className="text-slate-400"> [{u.sheet}]</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <Button onClick={applyWeightSheet} disabled={!wsFile || wsBusy}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="dba-ws-apply">
                {wsBusy ? 'Applying…' : 'Apply Weights'}
              </Button>
              <Button variant="outline" onClick={() => !wsBusy && setShowWeightSheet(false)} disabled={wsBusy}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Iter 222 — Corrected uploads history + undo */}
      {ccHistoryOpen && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" onClick={() => setCcHistoryOpen(false)}
             data-testid="dba-corrected-history-dialog">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-6 w-[680px] max-w-[95vw] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <History className="w-5 h-5 text-emerald-600" /> Corrected Upload History
              </h3>
              <button onClick={() => setCcHistoryOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            {ccHistory.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">No corrected uploads yet</p>
            ) : (
              <div className="space-y-2" data-testid="dba-corrected-history-list">
                {ccHistory.map(h => {
                  const dt = h.created_at ? new Date(h.created_at).toLocaleString() : '';
                  return (
                    <div key={h.id}
                         className={`border rounded-lg p-3 flex items-center justify-between gap-3 ${
                           h.undone ? 'border-slate-200 bg-slate-50' : 'border-emerald-200 bg-emerald-50/40'
                         }`}
                         data-testid={`dba-corrected-history-row-${h.id}`}>
                      <div className="text-xs text-slate-700 flex-1">
                        <div className="font-semibold text-slate-900">
                          {h.filename || '(no filename)'}
                          {h.undone && (
                            <span className="ml-2 inline-block px-1.5 py-[1px] rounded bg-slate-200 text-slate-600 text-[10px] font-bold">
                              UNDONE
                            </span>
                          )}
                        </div>
                        <div className="text-slate-500 mt-0.5">
                          {dt} · by {h.uploaded_by_name || h.uploaded_by_email}
                        </div>
                        <div className="text-slate-600 mt-0.5">
                          {h.inserted} new · {h.updated} overwritten
                          {h.weight_missing ? ` · ${h.weight_missing} without weight` : ''}
                          {h.used_bulk_fallback ? ' · bulk fallback' : ''}
                          {h.used_weight_sheet_fallback ? ' · weight sheet' : ''}
                        </div>
                      </div>
                      {!h.undone ? (
                        <Button size="sm" variant="outline"
                          onClick={() => undoCorrectedUpload(h.id)}
                          disabled={ccUndoBusy === h.id}
                          className="border-red-400 text-red-700 hover:bg-red-50"
                          data-testid={`dba-corrected-undo-${h.id}`}>
                          {ccUndoBusy === h.id ? (
                            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Trash2 className="w-3 h-3 mr-1" />
                          )}
                          Undo
                        </Button>
                      ) : (
                        <span className="text-[10px] text-slate-400">
                          {h.undo_deleted || 0} del · {h.undo_restored || 0} rst
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="md:flex-1 md:overflow-auto bg-white rounded-xl shadow-sm border border-slate-200">
        {records.length === 0 && !loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Database className="w-14 h-14 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-700 mb-1">{issueFilter ? 'No Errors Found' : 'No Records Found'}</h3>
              <p className="text-sm text-slate-400 mb-4">{issueFilter ? 'All records look good for this filter!' : 'Upload packing list files or adjust filters'}</p>
              {!issueFilter && (
                <div className="flex gap-2 justify-center">
                  <Button onClick={() => setShowUpload(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    <Upload className="w-4 h-4 mr-1.5" />Upload Files
                  </Button>
                  <Button variant="outline" onClick={handleBackfill}>
                    <RefreshCw className="w-4 h-4 mr-1.5" />Backfill from History
                  </Button>
                </div>
              )}
              {/* Where-did-this-parcel-go hint: if the search term
                  matches rows that were archived by an invoice
                  replacement, tell the user which invoice absorbed
                  them so they can re-run the search. */}
              {!issueFilter && searchHistoryHint && searchHistoryHint.count > 0 && (
                <div className="mt-6 max-w-xl mx-auto text-left bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 shadow-sm"
                     data-testid="dba-search-history-hint">
                  <div className="flex items-center gap-2 text-[12px] font-semibold text-amber-900 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Parcel may have been replaced
                  </div>
                  <div className="text-[11px] text-amber-800 space-y-1">
                    {searchHistoryHint.hits.slice(0, 3).map((h, i) => {
                      const dt = (h.replaced_at || '').slice(0, 10);
                      return (
                        <div key={i}>
                          Invoice&nbsp;<strong>{h.invoice_no}</strong> was replaced
                          {dt && <> on <strong>{dt}</strong></>}
                          {h.replaced_by && <> by <em>{h.replaced_by}</em></>} ·{' '}
                          previous tracking <span className="font-mono">{h.matched_row.track_no || '—'}</span>,
                          product <em>{h.matched_row.product || '—'}</em>.
                          <button
                            type="button"
                            onClick={() => { setSearchQuery(h.invoice_no); setPage(1); }}
                            className="ml-1 text-purple-700 hover:underline font-medium"
                            data-testid={`dba-search-history-hint-jump-${i}`}
                          >
                            Search invoice {h.invoice_no} →
                          </button>
                        </div>
                      );
                    })}
                    {searchHistoryHint.hits.length > 3 && (
                      <div className="text-[10px] text-amber-700 pt-0.5">
                        …+{searchHistoryHint.hits.length - 3} more replacement event(s) touched this term
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
          <DbaMobileCards records={records} onOpen={setMobileRec} isSelected={isSelected} />
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full border-collapse" data-testid="dba-table">
              <thead className="sticky top-0 z-10">
                <tr>
                  {['#', 'Invoice', 'Client Code', 'Tracking', '2nd Name', 'Qty', 'Customer Name', 'Country', 'Service', 'OK Parcel DT', 'Weight'].map(h => (
                    <th key={h} className="bg-[#1B4F72] text-white text-xs font-semibold py-2 px-2 text-left border border-slate-300 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => {
                  const isFirstOfTrack = i === 0 || records[i - 1]?.track_no !== r.track_no;
                  const isCancelled = !!r.cancelled;
                  const showWeight = isFirstOfTrack && r.weight;
                  const hasQtyErr = !isCancelled && r.qty === 0;
                  const hasWtErr = !isCancelled && isFirstOfTrack && r.weight === 0;
                  const hasSvcErr = !isCancelled && !r.service;
                  const hasDateErr = !isCancelled && !r.parcel_date;
                  const hasInvErr = !isCancelled && (!r.invoice_no || !/^A\d/.test(r.invoice_no));
                  const rowSelected = isSelected(r);
                  return (
                    <tr key={i} className={`hover:bg-indigo-50/40 transition-colors ${
                      rowSelected ? 'bg-red-100/70 hover:bg-red-100' :
                      isCancelled ? 'bg-pink-100/70 hover:bg-pink-100' :
                      (issueFilter && (hasQtyErr || hasWtErr || hasSvcErr) ? 'bg-red-50/50' : '')
                    }`}
                    data-testid={`dba-row-${i}`}
                    onMouseEnter={(e) => handleRowMouseEnter(r, e.currentTarget)}
                    onMouseLeave={handleRowMouseLeave}
                    >
                      <td
                        className={`py-1.5 px-2 text-xs border border-slate-200 text-center cursor-pointer select-none ${
                          rowSelected ? 'bg-red-300 text-red-900 font-bold' : 'text-slate-400 hover:bg-red-50 hover:text-red-700'
                        }`}
                        onClick={(e) => { e.stopPropagation(); toggleSelect(r); }}
                        title="Click # to select / deselect this row for deletion"
                        data-testid={`dba-rownum-${i}`}
                      >{(page-1)*LIMIT + i + 1}</td>
                      <DBAEditableCell
                        value={r.invoice_no || ''}
                        onSave={(v) => saveCellEdit(r, 'invoice_no', v)}
                        className={`py-1.5 px-2 text-xs font-semibold border border-slate-200 whitespace-nowrap ${hasInvErr ? 'text-red-500 bg-red-50' : 'text-slate-800'}`}
                        display={r.invoice_no || '-'}
                        testid={`dba-cell-invoice-${i}`}
                      />
                      <DBAEditableCell
                        value={r.client_code || ''}
                        onSave={(v) => saveCellEdit(r, 'client_code', v.toUpperCase())}
                        className="py-1.5 px-2 text-xs text-slate-600 border border-slate-200"
                        testid={`dba-cell-client-${i}`}
                      />
                      <DBATrackingCell rec={r} testid={`dba-track-${i}`}
                        onSaved={onTrackingSaved} />
                      <DBAEditableCell
                        value={r.product || ''}
                        onSave={(v) => saveCellEdit(r, 'product', v)}
                        className="py-1.5 px-2 text-xs text-slate-800 border border-slate-200 max-w-[200px] truncate"
                        testid={`dba-cell-product-${i}`}
                      />
                      <DBAEditableCell
                        value={r.qty ?? 0}
                        onSave={(v) => saveCellEdit(r, 'qty', v)}
                        className={`py-1.5 px-2 text-xs text-center font-bold border border-slate-200 ${hasQtyErr ? 'text-red-500 bg-red-50' : 'text-slate-900'}`}
                        inputType="number"
                        testid={`dba-cell-qty-${i}`}
                      />
                      <DBAEditableCell
                        value={r.customer_name || ''}
                        onSave={(v) => saveCellEdit(r, 'customer_name', v)}
                        className="py-1.5 px-2 text-xs text-slate-800 border border-slate-200 whitespace-nowrap"
                        testid={`dba-cell-customer-${i}`}
                      />
                      <DBAEditableCell
                        value={r.country || ''}
                        onSave={(v) => saveCellEdit(r, 'country', v)}
                        className="py-1.5 px-2 text-xs text-slate-600 border border-slate-200"
                        testid={`dba-cell-country-${i}`}
                      />
                      <DBAEditableCell
                        value={r.service || ''}
                        onSave={(v) => saveCellEdit(r, 'service', v.toUpperCase())}
                        className={`py-1.5 px-2 text-xs border border-slate-200 ${hasSvcErr ? 'bg-red-50' : ''}`}
                        display={r.service ? (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            r.service === 'EMS' || r.service === 'EMS-SERVICE' ? 'bg-amber-100 text-amber-700' :
                            r.service === 'CMS' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          }`}>{r.service}</span>
                        ) : <span className="text-red-400 text-[10px]">BLANK</span>}
                        testid={`dba-cell-service-${i}`}
                      />
                      <DBAEditableCell
                        value={r.parcel_date || ''}
                        onSave={(v) => saveCellEdit(r, 'parcel_date', v)}
                        className={`py-1.5 px-2 text-xs border border-slate-200 whitespace-nowrap ${hasDateErr ? 'text-red-400 bg-red-50' : 'text-slate-600'}`}
                        display={r.parcel_date || 'BLANK'}
                        placeholder="DD-Mon-YY"
                        testid={`dba-cell-date-${i}`}
                      />
                      {isFirstOfTrack ? (
                        <DBAEditableCell
                          value={isCancelled ? 'Cancelled' : (r.weight ?? 0)}
                          onSave={(v) => saveCellEdit(r, 'weight', v)}
                          className={`py-1.5 px-2 text-xs text-center border border-slate-200 ${
                            isCancelled ? 'bg-pink-100 text-pink-900 font-semibold' :
                            hasWtErr ? 'text-red-400 bg-red-50' : 'text-slate-600'
                          }`}
                          inputType="text"
                          display={
                            isCancelled
                              ? <button
                                  onClick={(e) => { e.stopPropagation(); openCancelPopover(r, e.currentTarget.closest('tr')); }}
                                  className="px-1.5 py-0.5 rounded bg-pink-200 text-pink-900 text-[10px] font-bold tracking-wide inline-flex items-center gap-1 hover:bg-pink-300 transition-colors"
                                  title={r.cancel_note ? 'View / edit cancellation reason' : 'Add cancellation reason'}
                                  data-testid={`dba-cancel-badge-${i}`}
                                >
                                  CANCELLED
                                  <StickyNote className={`w-3 h-3 ${r.cancel_note ? 'text-amber-500' : 'text-pink-700/60'}`} />
                                </button>
                              : (r.weight ? `${r.weight} gm` : <span className="text-red-400">0</span>)
                          }
                          placeholder="number or 'Cancelled'"
                          testid={`dba-cell-weight-${i}`}
                        />
                      ) : (
                        <td className="py-1.5 px-2 text-xs text-center border border-slate-200 text-slate-300"></td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
      <DbaScanDialog open={scanOpen} onClose={() => setScanOpen(false)} onCode={onScanCode} />
      <DbaRecordSheet record={mobileRec} onClose={() => setMobileRec(null)} onSave={saveCellEdit}
        onTrackingSaved={onTrackingSaved} onToggleSelect={toggleSelect} selected={mobileRec ? isSelected(mobileRec) : false} />

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-3 bg-white rounded-xl border border-slate-200 px-3 sm:px-4 py-2 gap-2 flex-wrap">
          <span className="text-[10px] sm:text-xs text-slate-500">{total} records | Page {page} of {pages}</span>
          <div className="flex gap-1 flex-wrap">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => fetchData(page - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {Array.from({ length: Math.min(5, pages) }, (_, i) => {
              const p = page <= 3 ? i + 1 : page + i - 2;
              if (p < 1 || p > pages) return null;
              return (
                <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm"
                  className={p === page ? 'bg-indigo-600 text-white' : ''}
                  onClick={() => fetchData(p)}>{p}</Button>
              );
            })}
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => fetchData(page + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* History Modal — audit log of every cell edit */}
      {historyOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center px-4"
          onClick={() => setHistoryOpen(false)}
          data-testid="dba-history-overlay"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50 rounded-t-lg flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-indigo-600" />
                <div>
                  <h3 className="font-semibold text-sm">Edit History</h3>
                  <p className="text-[11px] text-slate-500">Audit trail of cell edits — who, what, when. Date filter applies to the on-screen list AND the Excel download.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1 text-xs">
                  <CalendarRange className="w-3.5 h-3.5 text-slate-400" />
                  <input
                    type="date"
                    value={historyDateFrom}
                    onChange={(e) => setHistoryDateFrom(e.target.value)}
                    className="border border-slate-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    data-testid="dba-history-from"
                  />
                  <span className="text-slate-400">→</span>
                  <input
                    type="date"
                    value={historyDateTo}
                    onChange={(e) => setHistoryDateTo(e.target.value)}
                    className="border border-slate-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    data-testid="dba-history-to"
                  />
                  {(historyDateFrom || historyDateTo) && (
                    <button
                      onClick={() => { setHistoryDateFrom(''); setHistoryDateTo(''); setTimeout(fetchHistory, 0); }}
                      className="text-slate-400 hover:text-slate-600"
                      title="Clear date filter"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={historyTrackFilter}
                    onChange={(e) => setHistoryTrackFilter(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') fetchHistory(); }}
                    placeholder="Filter by tracking…"
                    className="pl-7 pr-2 py-1 text-xs border border-slate-200 rounded w-40 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    data-testid="dba-history-filter"
                  />
                </div>
                <Button size="sm" variant="outline" onClick={fetchHistory} className="h-7 px-2 text-xs" data-testid="dba-history-refresh">
                  <RefreshCw className="w-3 h-3 mr-1" /> Apply
                </Button>
                <Button
                  size="sm"
                  onClick={downloadHistoryExcel}
                  className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="dba-history-excel"
                  title="Download the filtered history as an Excel workbook"
                >
                  <Download className="w-3 h-3 mr-1" /> Excel
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (historyTrackFilter) params.set('track_no', historyTrackFilter);
                    if (historyDateFrom) params.set('date_from', historyDateFrom);
                    if (historyDateTo) params.set('date_to', historyDateTo);
                    window.open(`/database-a/history?${params.toString()}`, '_blank', 'noopener');
                  }}
                  className="h-7 px-2 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  data-testid="dba-history-open-tab"
                  title="Open this history view in a full-screen new tab"
                >
                  <ExternalLink className="w-3 h-3 mr-1" /> New tab
                </Button>
                <button onClick={() => setHistoryOpen(false)} className="p-1 rounded hover:bg-slate-200" data-testid="dba-history-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="overflow-auto p-3">
              {historyLoading ? (
                <div className="py-12 flex items-center justify-center text-slate-500 text-sm">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
                </div>
              ) : historyItems.length === 0 ? (
                <div className="py-12 text-center text-slate-500 text-sm">
                  No edits recorded yet. When a teammate double-clicks any cell in the table and saves, it appears here.
                </div>
              ) : (
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-slate-100 text-slate-600 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 border-b">When</th>
                      <th className="text-left px-2 py-1.5 border-b">By</th>
                      <th className="text-left px-2 py-1.5 border-b">Tracking</th>
                      <th className="text-left px-2 py-1.5 border-b">Field</th>
                      <th className="text-left px-2 py-1.5 border-b">Old → New</th>
                      <th className="text-left px-2 py-1.5 border-b">Invoice</th>
                      <th className="text-left px-2 py-1.5 border-b w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyItems.map((h) => (
                      <tr key={h.id} className="hover:bg-indigo-50/40" data-testid={`dba-history-row-${h.id}`}>
                        <td className="px-2 py-1.5 border-b whitespace-nowrap text-slate-500">
                          {(() => {
                            try {
                              const d = new Date(h.ts);
                              return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
                            } catch { return h.ts; }
                          })()}
                        </td>
                        <td className="px-2 py-1.5 border-b whitespace-nowrap">
                          <span className="font-medium text-slate-800">{h.user_name || h.user_email}</span>
                          {h.user_name && h.user_email && (
                            <div className="text-[10px] text-slate-400">{h.user_email}</div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 border-b font-mono text-slate-700">{h.track_no}</td>
                        <td className="px-2 py-1.5 border-b text-slate-700">
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wide">
                            {h.field}
                          </span>
                          {h.reverted_from && (
                            <span className="ml-1 px-1 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] uppercase">revert</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 border-b">
                          <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-700 line-through mr-1">
                            {String(h.old_value ?? '') || '∅'}
                          </span>
                          →
                          <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium">
                            {String(h.new_value ?? '') || '∅'}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 border-b text-slate-600">{h.invoice_no || '-'}</td>
                        <td className="px-2 py-1.5 border-b text-right">
                          <button
                            onClick={() => revertHistoryEntry(h)}
                            className="px-2 py-0.5 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-[10px] font-medium"
                            data-testid={`dba-history-revert-${h.id}`}
                            title={`Roll this field back to "${h.old_value ?? ''}" (logs as a new entry)`}
                          >
                            ↩ Revert
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel-reason popover (renders next to a cancelled row on hover/click) */}
      {cancelPopover && (
        <CancelNotePopover
          rec={cancelPopover.rec}
          anchorEl={cancelPopover.anchorEl}
          onClose={closeCancelPopover}
          onMouseEnter={cancelClose}
          onMouseLeave={() => scheduleClose(250)}
          onSaved={(noteText) => {
            // Mirror the new note onto every loaded sibling row of the same parcel
            setRecords(prev => prev.map(r =>
              r.track_no === cancelPopover.rec.track_no ? { ...r, cancel_note: noteText } : r
            ));
          }}
        />
      )}

      {/* Fix Blank Client Codes dialog — now driven by Emirates sheet upload */}
      <Dialog open={fixCcOpen} onOpenChange={setFixCcOpen}>
        <DialogContent className="max-w-2xl" data-testid="dba-fix-cc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 justify-between">
              <span className="flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-yellow-600" />
                Fix Blank Client Codes
              </span>
              <button
                type="button"
                onClick={() => {
                  const url = new URL(window.location.href);
                  url.searchParams.set('panel', 'database_a');
                  url.searchParams.set('fixBlank', '1');
                  window.open(url.toString(), '_blank');
                }}
                title="Open Database A in a new tab (dialog auto-opens)"
                className="text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-100"
                data-testid="dba-fix-cc-open-newtab"
              >
                <ExternalLink className="w-4 h-4" />
              </button>
            </DialogTitle>
            <div className="text-[11px] text-slate-500">
              Upload the Emirates courier sheet — tracking, weight, and client code
              are updated automatically for every matching Emirates parcel.
              Other courier services are untouched.
            </div>
          </DialogHeader>

          {/* Single one-click action — replaces the old per-invoice Apply buttons */}
          <div className="flex items-center justify-between gap-3 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
            <div className="text-xs text-slate-700">
              <div className="font-semibold">Emirates Auto-Sync</div>
              <div className="text-[11px] text-slate-500">
                XLSX (sheet name = invoice) or CSV. Columns: <code className="text-[10px]">TrackingNo</code>, <code className="text-[10px]">Weight</code>, <code className="text-[10px]">ReceiverContactName</code>.
              </div>
            </div>
            <Button size="sm" onClick={openEmiratesFilePicker}
                    disabled={emiratesUploading}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white h-8 px-3 text-xs"
                    data-testid="dba-upload-emirates-sheet">
              {emiratesUploading
                ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Syncing…</>
                : <><Upload className="w-3 h-3 mr-1" /> Upload Emirates Sheet</>}
            </Button>
            <input ref={emiratesFileRef} type="file" accept=".xlsx,.xlsm,.csv"
                   onChange={handleEmiratesFile} className="hidden"
                   data-testid="dba-emirates-file-input" />
            <input ref={perInvoiceFileRef} type="file" accept=".xlsx,.xlsm,.csv"
                   onChange={handlePerInvoiceFile} className="hidden"
                   data-testid="dba-per-invoice-file-input" />
          </div>

          {/* Order Sheet correction — fixes WRONG or blank client_codes
              per-customer (source of truth: the Order Sheet used to generate
              the packing list). Safer than sibling inference for invoices
              that mix MAYU / JSHE / etc. clients. */}
          <div className="mt-2 flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <div className="text-xs text-slate-700">
              <div className="font-semibold">Correct client codes from Order Sheet</div>
              <div className="text-[11px] text-slate-500">
                Upload the invoice's Order Sheet — every parcel's client code is
                rewritten from the sheet's per-customer <code className="text-[10px]">CLIENT CODE</code> column.
                Fixes both blank and previously mis-inferred codes.
              </div>
            </div>
            <Button size="sm" onClick={() => openOrderSheetFilePicker('')}
                    disabled={orderSheetUploading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-xs whitespace-nowrap"
                    data-testid="dba-upload-order-sheet-fix">
              {orderSheetUploading
                ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Fixing…</>
                : <><Upload className="w-3 h-3 mr-1" /> Fix From Order Sheet</>}
            </Button>
            <input ref={orderSheetFileRef} type="file" accept=".xlsx,.xlsm"
                   onChange={handleOrderSheetFile} className="hidden"
                   data-testid="dba-order-sheet-file-input" />
          </div>

          {/* EMI service classification fix — one-click sweep for legacy rows
              where EMI-prefixed trackings landed as EMS/blank */}
          <div className="mt-2 flex items-center justify-between gap-3 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
            <div className="text-xs text-slate-700">
              <div className="font-semibold">Fix EMI trackings labelled EMS</div>
              <div className="text-[11px] text-slate-500">
                Emirates auto-temp trackings starting with <code className="text-[10px]">EMI</code> were
                previously mis-classified as EMS. One-click sweep re-labels
                them to EMIRATES. Revertible from Bulk Edits.
              </div>
            </div>
            <Button size="sm" onClick={handleFixEmiService}
                    disabled={emiFixRunning}
                    className="bg-sky-600 hover:bg-sky-700 text-white h-8 px-3 text-xs whitespace-nowrap"
                    data-testid="dba-fix-emi-service-btn">
              {emiFixRunning
                ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sweeping…</>
                : <>Fix EMI → EMIRATES</>}
            </Button>
          </div>
          {emiFixResult && (
            <div className="mt-1 text-[11px] text-slate-600" data-testid="dba-fix-emi-service-result">
              Scanned <strong>{emiFixResult.scanned}</strong> ·
              re-labelled <strong className="text-sky-700 ml-1">{emiFixResult.fixed}</strong>
            </div>
          )}

          {orderSheetResult && (
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs" data-testid="dba-order-sheet-result">
              <div className="bg-slate-50 border border-slate-200 rounded p-2 text-center">
                <div className="text-slate-500 text-[10px]">Invoice</div>
                <div className="font-bold text-slate-800 text-sm">{orderSheetResult.invoice_no}</div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                <div className="text-emerald-700 text-[10px]">Rows corrected</div>
                <div className="font-bold text-emerald-800 text-base">{orderSheetResult.rows_updated}</div>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded p-2 text-center">
                <div className="text-rose-700 text-[10px]">Unmatched</div>
                <div className="font-bold text-rose-800 text-base">{orderSheetResult.unmatched_count}</div>
              </div>
            </div>
          )}
          {orderSheetResult?.unmatched_customers?.length > 0 && (
            <div className="mt-1 max-h-24 overflow-y-auto text-[11px] bg-rose-50/50 border border-rose-200 rounded p-2"
                 data-testid="dba-order-sheet-unmatched-list">
              <div className="font-semibold text-rose-800 mb-1">
                Order-sheet customers not found on invoice ({orderSheetResult.unmatched_customers.length}):
              </div>
              {orderSheetResult.unmatched_customers.map((u, i) => (
                <div key={i} className="text-slate-700">
                  <strong>{u.name}</strong> · code {u.client_code}
                  {u.reason && <span className="text-rose-600 ml-1">· {u.reason}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Result summary from last sync */}
          {emiratesResult && (
            <div className="mt-2 grid grid-cols-4 gap-2 text-xs" data-testid="dba-emirates-result">
              <div className="bg-slate-50 border border-slate-200 rounded p-2 text-center">
                <div className="text-slate-500 text-[10px]">Processed</div>
                <div className="font-bold text-slate-800 text-base">{emiratesResult.processed}</div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                <div className="text-emerald-700 text-[10px]">Updated</div>
                <div className="font-bold text-emerald-800 text-base">{emiratesResult.updated}</div>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded p-2 text-center">
                <div className="text-rose-700 text-[10px]">Unmatched</div>
                <div className="font-bold text-rose-800 text-base">{emiratesResult.unmatched_count}</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-center">
                <div className="text-amber-700 text-[10px]">Duplicates</div>
                <div className="font-bold text-amber-800 text-base">{emiratesResult.duplicates_count}</div>
              </div>
            </div>
          )}
          {emiratesResult?.unmatched?.length > 0 && (
            <div className="mt-2 max-h-32 overflow-y-auto text-[11px] bg-rose-50/50 border border-rose-200 rounded p-2"
                 data-testid="dba-emirates-unmatched-list">
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-rose-800">
                  Unmatched rows (first {emiratesResult.unmatched.length}
                  {emiratesResult.unmatched_count > emiratesResult.unmatched.length
                    ? ` of ${emiratesResult.unmatched_count}` : ""}):
                </div>
                <button
                  onClick={downloadUnmatchedCsv}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-white border border-rose-300 text-rose-700 hover:bg-rose-100"
                  data-testid="dba-emirates-download-unmatched">
                  <Download className="w-3 h-3" />
                  Download Unmatched.csv
                </button>
              </div>
              {emiratesResult.unmatched.map((u, i) => (
                <div key={i} className="text-slate-700">
                  <strong>{u.invoice_no || '(blank)'}</strong> — {u.customer || '(no name)'} — {u.tracking}
                  <span className="text-rose-600 ml-1">· {u.reason}</span>
                  {u.candidates_on_invoice?.length > 0 && (
                    <div className="text-[10px] text-slate-500 ml-2">
                      DB customers on this invoice: {u.candidates_on_invoice.slice(0, 5).join(', ')}
                      {u.candidates_on_invoice.length > 5 && ` (+${u.candidates_on_invoice.length - 5})`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {emiratesResult?.still_pending_count > 0 && (
            <div className="mt-2 max-h-40 overflow-y-auto text-[11px] bg-amber-50/60 border border-amber-200 rounded p-2"
                 data-testid="dba-emirates-still-pending-list">
              <div className="font-semibold text-amber-800 mb-1">
                Still pending after sync — {emiratesResult.still_pending_count} customer{emiratesResult.still_pending_count === 1 ? '' : 's'}
                (invoice has Emirates rows in DB but the sheet didn't cover them):
              </div>
              {Object.entries(emiratesResult.still_pending_by_invoice || {}).map(([inv, list]) => (
                <div key={inv} className="mb-1">
                  <strong className="text-slate-800">{inv}</strong>
                  <span className="text-slate-500"> · {list.length} customer{list.length === 1 ? '' : 's'}</span>
                  <div className="ml-2 text-slate-700">
                    {list.slice(0, 20).map((p, i) => (
                      <div key={i}>
                        · {p.customer}
                        {p.temp_tracking && <span className="text-[10px] text-slate-500 ml-1">(temp: {p.temp_tracking})</span>}
                      </div>
                    ))}
                    {list.length > 20 && (
                      <div className="text-[10px] text-slate-500">+{list.length - 20} more</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 border-t pt-3">
            <div className="text-[11px] font-semibold text-slate-600 mb-2">
              Pending blank-client-code invoices (view only — will resolve after Emirates sync)
            </div>
            {fixCcLoading ? (
              <div className="flex items-center justify-center py-6 text-slate-500 text-sm">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
              </div>
            ) : fixCcData.invoices.length === 0 ? (
              <div className="text-center py-4 text-slate-500 text-sm">
                <CheckCircle2 className="w-6 h-6 mx-auto mb-1 text-emerald-500" />
                No blank client codes remaining.
              </div>
            ) : (
              <div className="text-xs text-slate-500 mb-2">
                <strong>{fixCcData.total_blank}</strong> blank row{fixCcData.total_blank === 1 ? '' : 's'} across{' '}
                <strong>{fixCcData.invoices.length}</strong> invoice{fixCcData.invoices.length === 1 ? '' : 's'}.
                {fixCcData.invoices_with_suggestion > 0 && (
                  <> {fixCcData.invoices_with_suggestion} have a suggested code from sibling rows.</>
                )}
                <div className="max-h-[240px] overflow-y-auto space-y-1 pr-1 mt-2">
                  {fixCcData.invoices.map((inv, idx) => (
                    <div key={idx}
                         className="border border-slate-200 rounded p-2 flex items-center gap-2 text-[11px]"
                         data-testid={`fix-cc-row-${inv.invoice_no}`}>
                      <span className="font-semibold text-slate-800">{inv.invoice_no}</span>
                      {inv.latest_date && (
                        <span className="px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10px]"
                              title={`Newest parcel date on ${inv.invoice_no}`}
                              data-testid={`fix-cc-date-${inv.invoice_no}`}>
                          {inv.latest_date}
                        </span>
                      )}
                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                        {inv.blank_count} blank
                      </span>
                      {inv.sibling_codes?.length > 0 && (
                        <span className="text-emerald-700">
                          suggested: {inv.sibling_codes.join(', ')}
                        </span>
                      )}
                      {inv.sample_customers?.length > 0 && (
                        <span className="text-slate-500 truncate flex-1">
                          · {inv.sample_customers.slice(0, 2).join(', ')}
                        </span>
                      )}
                      <Button size="sm"
                              onClick={() => openPerInvoicePicker(inv.invoice_no)}
                              disabled={emiratesUploading || inv.invoice_no === "(blank invoice)"}
                              className="h-6 px-2 text-[10px] bg-yellow-600 hover:bg-yellow-700 text-white ml-auto"
                              data-testid={`fix-cc-upload-${inv.invoice_no}`}>
                        <Upload className="w-3 h-3 mr-1" />
                        Upload Sheet
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFixCcOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Edits history + one-click revert */}
      <Dialog open={bulkHistoryOpen} onOpenChange={setBulkHistoryOpen}>
        <DialogContent className="max-w-2xl" data-testid="dba-bulk-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-amber-600" />
              Recent Bulk Edits
            </DialogTitle>
            <div className="text-[11px] text-slate-500">
              Every Emirates sync, blank-client-code fix, or blank-date fix is recorded here.
              One click restores every row's old value.
            </div>
          </DialogHeader>

          {bulkHistoryLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
            </div>
          ) : bulkHistoryEntries.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              No bulk edits recorded yet. Any operation performed BEFORE this feature shipped
              cannot be auto-reverted — please clear affected cells manually by double-clicking
              them in the table.
            </div>
          ) : (
            <div className="max-h-[440px] overflow-y-auto space-y-2 pr-1">
              {bulkHistoryEntries.map(e => (
                <div key={e.id}
                     className={`border rounded-lg p-3 text-xs ${
                       e.reverted ? 'bg-slate-50 border-slate-200 opacity-70' : 'bg-white border-amber-200'
                     }`}
                     data-testid={`bulk-history-row-${e.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800">
                        {e.action}
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                          {(e.changes || []).length} field change{(e.changes || []).length === 1 ? '' : 's'}
                        </span>
                        {e.reverted && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                            Reverted
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {e.user_email || e.user_name || 'user'} · {new Date(e.created_at).toLocaleString()}
                        {e.meta?.filename && <> · <span className="italic">{e.meta.filename}</span></>}
                      </div>
                    </div>
                    {!e.reverted && (
                      <Button size="sm"
                              onClick={() => setBulkPreviewEntry(e)}
                              disabled={!!bulkReverting[e.id]}
                              className="h-7 px-3 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
                              data-testid={`revert-bulk-${e.id}`}>
                        {bulkReverting[e.id]
                          ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Reverting…</>
                          : <>Preview & Revert</>}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkHistoryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview & Revert confirmation dialog */}
      <Dialog open={!!bulkPreviewEntry} onOpenChange={(o) => { if (!o) setBulkPreviewEntry(null); }}>
        <DialogContent className="max-w-3xl" data-testid="dba-revert-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-amber-600" />
              Preview Revert
            </DialogTitle>
            <div className="text-[11px] text-slate-500">
              These are the exact old values that will be restored. Grouped by row / field.
            </div>
          </DialogHeader>

          {bulkPreviewEntry && (() => {
            const changes = bulkPreviewEntry.changes || [];
            // Group changes by target row (doc_id or _id)
            const byRow = {};
            for (const ch of changes) {
              const key = ch.doc_id || ch._id || '?';
              if (!byRow[key]) byRow[key] = [];
              byRow[key].push(ch);
            }
            const rowKeys = Object.keys(byRow);
            return (
              <>
                <div className="text-xs text-slate-600 mb-2">
                  <strong>{bulkPreviewEntry.action}</strong>
                  {' · '}
                  <strong>{rowKeys.length}</strong> row{rowKeys.length === 1 ? '' : 's'}
                  {' · '}
                  <strong>{changes.length}</strong> field change{changes.length === 1 ? '' : 's'}
                  {bulkPreviewEntry.meta?.invoice_no && (
                    <> · Invoice <strong>{bulkPreviewEntry.meta.invoice_no}</strong></>
                  )}
                  {bulkPreviewEntry.meta?.filename && (
                    <> · From <span className="italic">{bulkPreviewEntry.meta.filename}</span></>
                  )}
                </div>
                <div className="max-h-[380px] overflow-y-auto border rounded">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="p-1.5 text-left border-b">#</th>
                        <th className="p-1.5 text-left border-b">Field</th>
                        <th className="p-1.5 text-left border-b">Current (will change TO)</th>
                        <th className="p-1.5 text-left border-b text-emerald-700">Will restore TO</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rowKeys.map((rowKey, ri) => (
                        byRow[rowKey].map((ch, ci) => (
                          <tr key={`${rowKey}-${ci}`} className={ci === 0 ? 'border-t-2 border-slate-200' : ''}>
                            {ci === 0 && (
                              <td className="p-1.5 align-top font-semibold text-slate-600"
                                  rowSpan={byRow[rowKey].length}>
                                {ri + 1}
                              </td>
                            )}
                            <td className="p-1.5 font-mono text-slate-700">{ch.field}</td>
                            <td className="p-1.5 text-rose-700 font-mono">{String(ch.new ?? '')}</td>
                            <td className="p-1.5 text-emerald-700 font-mono">{String(ch.old ?? '')}</td>
                          </tr>
                        ))
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkPreviewEntry(null)}>Cancel</Button>
            <Button
              onClick={async () => {
                const id = bulkPreviewEntry?.id;
                setBulkPreviewEntry(null);
                if (id) await revertBulkEntry(id);
              }}
              disabled={!bulkPreviewEntry || bulkReverting[bulkPreviewEntry?.id]}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="dba-confirm-revert">
              Confirm Revert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fix Blank Dates dialog */}
      <Dialog open={fixDateOpen} onOpenChange={setFixDateOpen}>
        <DialogContent className="max-w-2xl" data-testid="dba-fix-date-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-pink-600" />
              Fix Blank Parcel Dates
            </DialogTitle>
          </DialogHeader>

          {fixDateLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading invoices…
            </div>
          ) : fixDateData.invoices.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
              No blank dates remaining — nice work.
            </div>
          ) : (
            <>
              <div className="text-xs text-slate-500 mb-2">
                <strong>{fixDateData.total_blank}</strong> blank row{fixDateData.total_blank === 1 ? '' : 's'} across
                {' '}<strong>{fixDateData.invoices.length}</strong> invoice{fixDateData.invoices.length === 1 ? '' : 's'}.
                {fixDateData.invoices_with_suggestion > 0 && (
                  <> {fixDateData.invoices_with_suggestion} have a suggested date from sibling rows.</>
                )}
              </div>
              <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
                {fixDateData.invoices.map((inv, idx) => {
                  const blankInvoice = inv.invoice_no === "(blank invoice)";
                  const override = fixDateOverrides[inv.invoice_no] ?? '';
                  const applying = !!fixDateApplying[inv.invoice_no];
                  const overrideApplyable = override && !applying && !blankInvoice;
                  return (
                    <div key={idx}
                         className={`border rounded-lg p-3 flex items-start gap-3 ${
                           blankInvoice ? 'bg-slate-50 border-slate-200' : 'bg-pink-50 border-pink-200'
                         }`}
                         data-testid={`fix-date-row-${inv.invoice_no}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-800">
                          {inv.invoice_no}
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-white border text-slate-600">
                            {inv.blank_count} blank
                          </span>
                        </div>
                        {inv.sample_customers?.length > 0 && (
                          <div className="text-[11px] text-slate-500 truncate">
                            {inv.sample_customers.join(', ')}
                          </div>
                        )}
                        {inv.sibling_dates?.length > 0 && (
                          <div className="text-[11px] text-slate-600 mt-1">
                            Suggested dates:{' '}
                            {inv.sibling_dates.map((sd, si) => (
                              <button key={si}
                                      onClick={() => !applying && !blankInvoice && applyFixDate(inv.invoice_no, sd)}
                                      disabled={applying || blankInvoice}
                                      className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold hover:bg-emerald-200 disabled:opacity-50"
                                      data-testid={`fix-date-suggest-${sd.parcel_date}`}>
                                {sd.parcel_date}{sd.from === 'track' ? ' (tracking)' : ''}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="e.g. 17-Jul-26"
                          value={override}
                          onChange={(e) => setFixDateOverrides(o => ({ ...o, [inv.invoice_no]: e.target.value }))}
                          disabled={blankInvoice}
                          className="h-8 w-32 text-xs"
                          data-testid={`fix-date-input-${inv.invoice_no}`}
                        />
                        <Button size="sm"
                                onClick={() => applyFixDate(inv.invoice_no, override)}
                                disabled={!overrideApplyable}
                                className="h-8 px-3 text-xs bg-pink-600 hover:bg-pink-700 text-white"
                                data-testid={`fix-date-apply-${inv.invoice_no}`}>
                          {applying
                            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Applying…</>
                            : <>Apply</>}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setFixDateOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Duplicate Cards — manual verification workflow */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-4xl" data-testid="dba-merge-dup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-600" />
              Verify &amp; Merge Duplicate Cards
            </DialogTitle>
            <div className="text-[11px] text-slate-500">
              {dupMode === 'cross_date' ? (
                <>
                  <strong className="text-purple-700">Cross-date mode:</strong> grouped by
                  invoice&nbsp;number, tracking&nbsp;number, product, and quantity —
                  <em> ignoring parcel date</em>. Use this to collapse the same
                  invoice that accidentally landed on two dates. Tick the
                  duplicates to remove; the Original keeps its data.
                </>
              ) : dupMode === 'same_tracking' ? (
                <>
                  <strong className="text-purple-700">Same-tracking mode:</strong> grouped by
                  customer&nbsp;+ tracking&nbsp;number — <em>ignoring invoice, product,
                  qty and date</em>. Surfaces cases where a packing list was
                  regenerated with a corrected invoice AND product but the old
                  row was never removed. Temp <code className="text-[10px]">EMI#####</code>
                  placeholders are excluded. Use "keep&nbsp;this" to elect the
                  correct row and tick the outdated one for removal.
                </>
              ) : (
                <>
                  Duplicates are grouped by all 5 fields: date, customer,
                  tracking&nbsp;number, product, and quantity. Rows with
                  different tracking numbers are treated as separate parcels —
                  never merged. Tick the duplicates you want to remove, then
                  click Merge Selected; the Original keeps its tracking,
                  weight, client code, status, and notes.
                </>
              )}
            </div>
            <div className="flex items-center gap-1 mt-2" data-testid="dba-merge-dup-mode-tabs">
              <button
                type="button"
                onClick={() => !dupLoading && dupMode !== 'default' && mergeDuplicates('default', dupFrom, dupTo)}
                disabled={dupLoading}
                className={`text-[11px] px-2.5 py-1 rounded border transition ${
                  dupMode === 'default'
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                } disabled:opacity-50`}
                data-testid="dba-merge-dup-mode-default"
              >
                Same-day duplicates
              </button>
              <button
                type="button"
                onClick={() => !dupLoading && dupMode !== 'cross_date' && mergeDuplicates('cross_date', dupFrom, dupTo)}
                disabled={dupLoading}
                className={`text-[11px] px-2.5 py-1 rounded border transition ${
                  dupMode === 'cross_date'
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                } disabled:opacity-50`}
                data-testid="dba-merge-dup-mode-crossdate"
                title="Find rows on the same invoice + tracking that landed on different dates"
              >
                Cross-date (same invoice + tracking)
              </button>
              <button
                type="button"
                onClick={() => !dupLoading && dupMode !== 'same_tracking' && mergeDuplicates('same_tracking', dupFrom, dupTo)}
                disabled={dupLoading}
                className={`text-[11px] px-2.5 py-1 rounded border transition ${
                  dupMode === 'same_tracking'
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                } disabled:opacity-50`}
                data-testid="dba-merge-dup-mode-sametracking"
                title="Find rows sharing the SAME tracking on the same customer, regardless of invoice / product / qty / date"
              >
                Same tracking (any version)
              </button>
            </div>
            {/* Iter 251 — monthly window: limit the scan to a from/to date
                range so one month's duplicates can be cleared at a time. */}
            <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="dba-merge-dup-dates">
              <span className="text-[11px] text-slate-500 font-medium">Parcel date:</span>
              <input type="date" value={dupFrom} disabled={dupLoading}
                     onChange={e => mergeDuplicates(dupMode, e.target.value, dupTo)}
                     className="px-2 py-1 border border-slate-300 rounded text-[11px] disabled:opacity-50"
                     data-testid="dba-merge-dup-from" />
              <span className="text-[11px] text-slate-400">to</span>
              <input type="date" value={dupTo} disabled={dupLoading}
                     onChange={e => mergeDuplicates(dupMode, dupFrom, e.target.value)}
                     className="px-2 py-1 border border-slate-300 rounded text-[11px] disabled:opacity-50"
                     data-testid="dba-merge-dup-to" />
              {(dupFrom || dupTo) && (
                <button type="button"
                        onClick={() => mergeDuplicates(dupMode, '', '')}
                        disabled={dupLoading}
                        className="text-[11px] text-purple-700 hover:underline disabled:opacity-50"
                        data-testid="dba-merge-dup-clear-dates">
                  Clear (all dates)
                </button>
              )}
              {!dupFrom && !dupTo && (
                <span className="text-[10px] text-slate-400">all dates</span>
              )}
            </div>
          </DialogHeader>

          {dupLoading ? (
            <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Scanning Database A…
            </div>
          ) : dupData.group_count === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm" data-testid="dba-merge-dup-empty">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
              No duplicate groups found. Database A is clean.
            </div>
          ) : (
            <>
              {/* Search bar — filters groups by tracking, product,
                  customer, or invoice. Client-side, live. */}
              <div className="relative mb-2">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={dupSearch}
                  onChange={(e) => setDupSearch(e.target.value)}
                  placeholder="Search parcel by tracking, product, customer, or invoice…"
                  className="w-full text-xs border border-slate-300 rounded pl-8 pr-8 py-1.5 focus:outline-none focus:ring-1 focus:ring-purple-400 focus:border-purple-400"
                  data-testid="dba-merge-dup-search"
                />
                {dupSearch && (
                  <button
                    type="button"
                    onClick={() => setDupSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs"
                    aria-label="Clear search"
                    data-testid="dba-merge-dup-search-clear"
                  >
                    ✕
                  </button>
                )}
              </div>

              {(() => {
                const q = dupSearch.trim().toLowerCase().replace(/\s+/g, '');
                const filteredGroups = q
                  ? (dupData.groups || []).filter(g => {
                      const hay = [
                        g.key?.track_no, g.key?.product, g.key?.invoice_no,
                        g.key?.customer_name, String(g.key?.qty ?? ''),
                        ...(g.records || []).flatMap(r => [
                          r.track_no, r.product, r.invoice_no,
                          r.customer_name, r.client_code, r.parcel_date,
                          String(r.qty ?? ''),
                        ]),
                      ].map(s => String(s || '').toLowerCase().replace(/\s+/g, '')).join('|');
                      return hay.includes(q);
                    })
                  : (dupData.groups || []);
                const filteredDupCount = filteredGroups.reduce(
                  (a, g) => a + (g.records || []).filter(r => r.role === 'duplicate').length, 0);
                return (
                  <>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="text-xs text-slate-600" data-testid="dba-merge-dup-summary">
                        {q ? (
                          <>
                            Showing <strong>{filteredGroups.length}</strong> of <strong>{dupData.group_count}</strong> group{filteredGroups.length === 1 ? '' : 's'} ·
                            <strong className="ml-1">{filteredDupCount}</strong> matching duplicate{filteredDupCount === 1 ? '' : 's'} ·
                            <strong className="ml-1 text-purple-700">{dupSelectedCount}</strong> ticked
                          </>
                        ) : (
                          <>
                            <strong>{dupData.group_count}</strong> duplicate group{dupData.group_count === 1 ? '' : 's'} ·
                            <strong className="ml-1">{dupData.total_duplicate_records}</strong> extra record{dupData.total_duplicate_records === 1 ? '' : 's'} available for removal ·
                            <strong className="ml-1 text-purple-700">{dupSelectedCount}</strong> ticked
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" onClick={dupSelectAll}
                                className="h-7 px-2 text-xs" data-testid="dba-merge-dup-select-all">
                          Select all duplicates
                        </Button>
                        <Button size="sm" variant="outline" onClick={dupDeselectAll}
                                className="h-7 px-2 text-xs" data-testid="dba-merge-dup-deselect-all">
                          Deselect all
                        </Button>
                      </div>
                    </div>

                    <div className="max-h-[440px] overflow-y-auto pr-1 space-y-2">
                      {filteredGroups.length === 0 && q && (
                        <div className="text-center py-8 text-slate-500 text-xs" data-testid="dba-merge-dup-no-search-match">
                          No parcels match “<strong>{dupSearch}</strong>”.
                          <button type="button" onClick={() => setDupSearch('')}
                                  className="ml-2 text-purple-600 hover:underline">
                            Clear search
                          </button>
                        </div>
                      )}
                      {filteredGroups.map((g) => {
                        const gi = (dupData.groups || []).indexOf(g);
                        const isOpen = !!dupExpanded[gi];
                        const dupCount = g.records.filter(r => r.role === 'duplicate').length;
                        const tickedInGroup = g.records.filter(r => r.role === 'duplicate' && dupChecked[r.id]).length;
                        return (
                          <div key={gi} className="border border-slate-200 rounded-lg"
                               data-testid={`dba-merge-dup-group-${gi}`}>
                            <button type="button"
                                    onClick={() => setDupExpanded(prev => ({ ...prev, [gi]: !prev[gi] }))}
                                    className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-t-lg text-left text-xs">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-slate-400 text-[10px]">
                                  {isOpen ? '▼' : '▶'}
                                </span>
                                <span className="font-semibold text-slate-800 truncate">
                                  {g.records[0]?.customer_name || '?'} · {g.records[0]?.product || '?'}
                                  {g.records[0]?.invoice_no && (
                                    <span className="text-slate-500 font-normal ml-1">
                                      (inv {g.records[0].invoice_no})
                                    </span>
                                  )}
                                </span>
                                <span className="text-slate-500">qty {g.key.qty}</span>
                                <span className="text-slate-500 font-mono text-[10px] truncate">
                                  {g.key.track_no}
                                </span>
                                {g.records[0]?.parcel_date && (
                                  <span className="text-slate-500">· {g.records[0].parcel_date}</span>
                                )}
                              </div>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 whitespace-nowrap">
                          {tickedInGroup}/{dupCount} duplicate{dupCount === 1 ? '' : 's'} ticked
                        </span>
                      </button>
                      {isOpen && (
                        <div className="text-[11px]">
                          <table className="w-full">
                            <thead className="bg-white text-slate-500 border-y border-slate-100">
                              <tr>
                                <th className="w-8 py-1"></th>
                                <th className="text-left px-2">Role</th>
                                <th className="text-left px-2">Date</th>
                                <th className="text-left px-2">Tracking</th>
                                <th className="text-right px-2">Weight</th>
                                <th className="text-left px-2">Client&nbsp;Code</th>
                                <th className="text-left px-2">Created</th>
                              </tr>
                            </thead>
                            <tbody>
                              {g.records.map(rec => {
                                const isOrig = rec.role === 'original';
                                const checked = isOrig ? true : !!dupChecked[rec.id];
                                return (
                                  <tr key={rec.id}
                                      className={`border-t border-slate-100 ${isOrig ? 'bg-emerald-50/30' : 'bg-white'}`}
                                      data-testid={`dba-merge-dup-row-${rec.id}`}>
                                    <td className="w-8 py-1 text-center">
                                      <input type="checkbox"
                                             checked={checked}
                                             disabled={isOrig}
                                             onChange={() => !isOrig && dupToggle(rec.id)}
                                             data-testid={`dba-merge-dup-check-${rec.id}`}
                                             className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" />
                                    </td>
                                    <td className="px-2">
                                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                        isOrig ? 'bg-emerald-100 text-emerald-700'
                                               : 'bg-amber-100 text-amber-700'
                                      }`}>
                                        {isOrig ? 'Original' : 'Duplicate'}
                                      </span>
                                      {!isOrig && (
                                        <button
                                          type="button"
                                          onClick={() => dupSetOriginal(gi, rec.id)}
                                          className="ml-1 text-[9px] text-purple-600 hover:text-purple-800 underline underline-offset-2"
                                          data-testid={`dba-merge-dup-set-original-${rec.id}`}
                                          title="Keep this row instead — mark it as the Original"
                                        >
                                          keep&nbsp;this
                                        </button>
                                      )}
                                    </td>
                                    <td className="px-2 text-slate-600 text-[10px] whitespace-nowrap">
                                      {rec.parcel_date || <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="px-2 font-mono text-[10px]">{rec.track_no || '—'}</td>
                                    <td className="px-2 text-right">{rec.weight || 0}</td>
                                    <td className="px-2">{rec.client_code || '—'}</td>
                                    <td className="px-2 text-slate-500 text-[10px]">
                                      {(rec.created_at || '').slice(0, 10)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
                  </>
                );
              })()}
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDupOpen(false)}
                    disabled={merging}
                    data-testid="dba-merge-dup-cancel">
              Cancel
            </Button>
            <Button onClick={dupApply}
                    disabled={merging || dupSelectedCount === 0 || dupData.group_count === 0}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="dba-merge-dup-apply">
              {merging
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Merging…</>
                : <>Merge Selected ({dupSelectedCount})</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Bulk Purge dialog (Iter 137) */}
      <Dialog open={adminPurgeOpen} onOpenChange={(o) => {
        setAdminPurgeOpen(o);
        if (!o) { setAdminPurgePreview(null); setRestorePassword(''); }
        else { fetchPurgeBatches(); fetchPurgePwdMeta(); }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dba-admin-purge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" /> Admin Bulk Purge — Database A
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {/* Iter 179 — password rotation link */}
            <div className="flex items-center justify-between rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px]"
                 data-testid="dba-purge-pwd-meta">
              <span className="text-slate-600">
                Supervisor password:&nbsp;
                <strong>{pwdRotateMeta?.is_custom ? 'Custom (rotated)' : 'Default (seed)'}</strong>
                {pwdRotateMeta?.updated_at && (
                  <span className="text-slate-400"> · last rotated {new Date(pwdRotateMeta.updated_at).toLocaleString()}</span>
                )}
              </span>
              <button type="button" onClick={() => setPwdRotateOpen(true)}
                className="text-blue-700 hover:underline font-medium"
                data-testid="dba-purge-rotate-pwd-btn">
                Rotate password
              </button>
            </div>

            {/* Iter 178/179 — mode toggle: custom | day | restore */}
            <div className="flex gap-2" data-testid="dba-purge-mode-toggle">
              <button type="button"
                onClick={() => { setPurgeMode('custom'); setAdminPurgePreview(null); }}
                className={`flex-1 text-xs px-3 py-1.5 rounded border font-medium transition ${
                  purgeMode === 'custom'
                    ? 'bg-slate-700 text-white border-slate-700'
                    : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                }`}
                data-testid="dba-purge-mode-custom">
                Custom Filters
              </button>
              <button type="button"
                onClick={() => { setPurgeMode('day'); setAdminPurgePreview(null); }}
                className={`flex-1 text-xs px-3 py-1.5 rounded border font-medium transition ${
                  purgeMode === 'day'
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-white text-red-600 border-red-300 hover:bg-red-50'
                }`}
                data-testid="dba-purge-mode-day">
                Delete Entire Day
              </button>
              <button type="button"
                onClick={() => { setPurgeMode('restore'); setAdminPurgePreview(null); fetchPurgeBatches(); }}
                className={`flex-1 text-xs px-3 py-1.5 rounded border font-medium transition ${
                  purgeMode === 'restore'
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50'
                }`}
                data-testid="dba-purge-mode-restore">
                Restore (24h)
              </button>
            </div>

            {purgeMode === 'restore' ? (
              <div className="space-y-2" data-testid="dba-purge-restore-panel">
                <div className="rounded border-2 border-emerald-400 bg-emerald-50 text-emerald-800 p-3 text-xs">
                  <strong>Restore purged rows.</strong> Any admin bulk purge from the last 24 hours can be undone here.
                  Rows are re-inserted from the audit snapshot; anything you've re-added since is preserved (skipped).
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Supervisor Password *</label>
                  <Input type="password" value={restorePassword}
                    placeholder="Enter supervisor password"
                    onChange={(e) => setRestorePassword(e.target.value)}
                    data-testid="dba-purge-restore-password"
                    autoComplete="new-password" />
                </div>
                {purgeBatches.length === 0 ? (
                  <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500 text-center"
                       data-testid="dba-purge-restore-empty">
                    No purge batches in the last 24 hours.
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto border border-emerald-200 rounded"
                       data-testid="dba-purge-restore-list">
                    <table className="w-full text-[11px]">
                      <thead className="bg-emerald-100 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-1">When</th>
                          <th className="text-left px-2 py-1">By</th>
                          <th className="text-left px-2 py-1">Rows</th>
                          <th className="text-left px-2 py-1">Services</th>
                          <th className="text-left px-2 py-1">Dates</th>
                          <th className="text-right px-2 py-1">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purgeBatches.map(b => (
                          <tr key={b.purge_batch_id} className="border-t border-emerald-100"
                              data-testid={`dba-purge-batch-${b.purge_batch_id.slice(0, 8)}`}>
                            <td className="px-2 py-1 whitespace-nowrap">{new Date(b.ts).toLocaleString()}</td>
                            <td className="px-2 py-1">{b.user_email}</td>
                            <td className="px-2 py-1 font-semibold">{b.count}</td>
                            <td className="px-2 py-1">{b.services.join(', ')}</td>
                            <td className="px-2 py-1">{b.dates.slice(0, 3).join(', ')}{b.dates.length > 3 ? '…' : ''}</td>
                            <td className="px-2 py-1 text-right">
                              <Button size="sm" variant="outline"
                                onClick={() => runRestoreBatch(b.purge_batch_id)}
                                disabled={!restorePassword || restoringBatch === b.purge_batch_id}
                                data-testid={`dba-purge-restore-btn-${b.purge_batch_id.slice(0, 8)}`}
                                className="h-6 text-[11px] border-emerald-400 text-emerald-700 hover:bg-emerald-50">
                                {restoringBatch === b.purge_batch_id
                                  ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Restoring…</>
                                  : <>Restore</>}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : purgeMode === 'day' ? (
              <>
                <div className="rounded border-2 border-red-400 bg-red-50 text-red-800 p-3 text-xs" data-testid="dba-purge-day-warning">
                  <strong>Delete Entire Day.</strong> Every Database A row for the selected
                  date will be permanently removed — <strong>all services, all customers</strong>.
                  This cannot be undone. Preview first; every row is audit-logged.
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Date to wipe *</label>
                    <Input type="date" value={purgeDay}
                      onChange={(e) => { setPurgeDay(e.target.value); setAdminPurgePreview(null); }}
                      data-testid="dba-purge-day-date" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Supervisor Password *</label>
                    <Input type="password" value={adminPurge.password}
                      placeholder="Enter supervisor password"
                      onChange={(e) => setAdminPurge(p => ({ ...p, password: e.target.value }))}
                      data-testid="dba-purge-day-password"
                      autoComplete="new-password" />
                  </div>
                </div>
              </>
            ) : (
              <>
            <div className="rounded border border-red-200 bg-red-50 text-red-800 p-2 text-xs">
              <strong>Destructive.</strong> Rows matched by the filters below are permanently
              removed. Every row is audit-logged. Preview first — the real delete only runs
              when you press <em>Delete Matched Rows</em>.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Date From</label>
                <Input type="date" value={adminPurge.date_from}
                  onChange={(e) => setAdminPurge(p => ({ ...p, date_from: e.target.value }))}
                  data-testid="dba-purge-date-from" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Date To</label>
                <Input type="date" value={adminPurge.date_to}
                  onChange={(e) => setAdminPurge(p => ({ ...p, date_to: e.target.value }))}
                  data-testid="dba-purge-date-to" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Client / Customer Name (contains)</label>
                <Input value={adminPurge.customer_name}
                  placeholder="e.g. ANIL SHARMA"
                  onChange={(e) => setAdminPurge(p => ({ ...p, customer_name: e.target.value }))}
                  data-testid="dba-purge-customer" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Service (comma-separated: EMIRATES, ITPS, CMS…)</label>
                <Input value={adminPurge.service}
                  placeholder="ITPS,EMIRATES"
                  onChange={(e) => setAdminPurge(p => ({ ...p, service: e.target.value }))}
                  data-testid="dba-purge-service" />
                {servicesList.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {servicesList.slice(0, 10).map(s => (
                      <button key={s} type="button"
                        onClick={() => setAdminPurge(p => {
                          const cur = new Set(p.service.split(',').map(x => x.trim()).filter(Boolean));
                          if (cur.has(s)) cur.delete(s); else cur.add(s);
                          return { ...p, service: Array.from(cur).join(',') };
                        })}
                        className={`text-[10px] px-2 py-0.5 rounded-full border transition ${
                          adminPurge.service.split(',').map(x => x.trim()).includes(s)
                            ? 'bg-red-100 border-red-300 text-red-700'
                            : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                        }`}
                        data-testid={`dba-purge-service-chip-${s.toLowerCase()}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Supervisor Password *</label>
                <Input type="password" value={adminPurge.password}
                  placeholder="Enter supervisor password"
                  onChange={(e) => setAdminPurge(p => ({ ...p, password: e.target.value }))}
                  data-testid="dba-purge-password"
                  autoComplete="new-password" />
              </div>
            </div>
              </>
            )}

            {adminPurgePreview && (
              <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs" data-testid="dba-purge-preview">
                <div className="font-semibold text-amber-800 mb-1">
                  {adminPurgePreview.matched} row{adminPurgePreview.matched === 1 ? '' : 's'} match. Showing first {adminPurgePreview.preview?.length || 0}:
                </div>
                {adminPurgePreview.service_breakdown?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-1.5" data-testid="dba-purge-breakdown">
                    {adminPurgePreview.service_breakdown.map(b => (
                      <span key={b.service}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 font-semibold"
                        data-testid={`dba-purge-breakdown-${(b.service || 'blank').toLowerCase()}`}>
                        {b.service}: {b.count}
                      </span>
                    ))}
                  </div>
                )}
                {adminPurgePreview.preview?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto border border-amber-200 rounded bg-white">
                    <table className="w-full text-[11px]">
                      <thead className="bg-amber-100 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-1">Track #</th>
                          <th className="text-left px-2 py-1">Customer</th>
                          <th className="text-left px-2 py-1">Service</th>
                          <th className="text-left px-2 py-1">Date</th>
                          <th className="text-left px-2 py-1">Product</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminPurgePreview.preview.map((p, i) => (
                          <tr key={i} className="border-t border-amber-100">
                            <td className="px-2 py-1 font-mono">{p.track_no || '—'}</td>
                            <td className="px-2 py-1">{p.customer_name}</td>
                            <td className="px-2 py-1">{p.service}</td>
                            <td className="px-2 py-1">{p.parcel_date}</td>
                            <td className="px-2 py-1">{p.product}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setAdminPurgeOpen(false)} disabled={adminPurgeRunning}>Cancel</Button>
            {purgeMode !== 'restore' && (
              <>
            <Button variant="outline" onClick={() => runAdminPurge(true)}
              disabled={adminPurgeRunning}
              data-testid="dba-purge-preview-btn">
              {adminPurgeRunning
                ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Checking…</>
                : <>Preview</>}
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => runAdminPurge(false)}
              disabled={adminPurgeRunning || !adminPurgePreview || adminPurgePreview.matched === 0}
              title={!adminPurgePreview ? 'Run Preview first' : `Delete ${adminPurgePreview.matched} rows`}
              data-testid="dba-purge-delete-btn">
              {adminPurgeRunning
                ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Deleting…</>
                : <><Trash2 className="w-3 h-3 mr-1" /> Delete Matched Rows</>}
            </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 179 — Rotate supervisor purge password */}
      <Dialog open={pwdRotateOpen} onOpenChange={(o) => { setPwdRotateOpen(o); if (!o) setPwdRotate({ current: '', next: '', confirm: '' }); }}>
        <DialogContent className="max-w-md" data-testid="dba-purge-rotate-dialog">
          <DialogHeader>
            <DialogTitle>Rotate Supervisor Purge Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800">
              Ops uses this password to authorize every Admin Bulk Purge. Rotate it whenever a supervisor changes.
              The change is instant and audit-logged.
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Current Password *</label>
              <Input type="password" value={pwdRotate.current}
                onChange={(e) => setPwdRotate(p => ({ ...p, current: e.target.value }))}
                data-testid="dba-purge-rotate-current"
                autoComplete="current-password" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">New Password * (min 4 chars)</label>
              <Input type="password" value={pwdRotate.next}
                onChange={(e) => setPwdRotate(p => ({ ...p, next: e.target.value }))}
                data-testid="dba-purge-rotate-new"
                autoComplete="new-password" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Confirm New Password *</label>
              <Input type="password" value={pwdRotate.confirm}
                onChange={(e) => setPwdRotate(p => ({ ...p, confirm: e.target.value }))}
                data-testid="dba-purge-rotate-confirm"
                autoComplete="new-password" />
            </div>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setPwdRotateOpen(false)} disabled={pwdRotateBusy}>Cancel</Button>
            <Button onClick={runPwdRotate} disabled={pwdRotateBusy}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="dba-purge-rotate-save">
              {pwdRotateBusy
                ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Rotating…</>
                : <>Rotate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
};

// ── Editable cell ─────────────────────────────────────────────────────────────
// Read-only by default; double-click to edit. Enter to save, Escape to cancel.
const DBAEditableCell = ({ value, onSave, className = '', display, inputType = 'text', placeholder, testid }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const commit = async () => {
    if (String(draft) === String(value)) { setEditing(false); return; }
    setSaving(true);
    const ok = await onSave(draft);
    setSaving(false);
    if (ok) setEditing(false);
  };

  if (editing) {
    return (
      <td className={`${className} p-0 relative`} title="Press Enter to save, Esc to cancel">
        <input
          autoFocus
          type={inputType}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            else if (e.key === 'Escape') { setDraft(value); setEditing(false); }
          }}
          placeholder={placeholder}
          disabled={saving}
          className="w-full px-2 py-1 text-xs bg-amber-50 outline-none ring-2 ring-amber-400 disabled:opacity-60"
          data-testid={testid ? `${testid}-input` : undefined}
        />
      </td>
    );
  }

  return (
    <td
      className={`${className} cursor-text`}
      onDoubleClick={() => setEditing(true)}
      title="Double-click to edit"
      data-testid={testid}
    >
      {display !== undefined ? display : (value || <span className="text-slate-300">—</span>)}
    </td>
  );
};

export default DatabaseAPanel;
